import React, { useEffect, useState, useCallback } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import mn from "date-fns/locale/mn";

import {
    getAuth,
    signOut,
    onAuthStateChanged
} from "firebase/auth";

import {
    getFunctions,
    httpsCallable
} from "firebase/functions";

import {
    doc,
    updateDoc,
    getFirestore,
    collection,
    query,
    where,
    addDoc,
    getDoc,
    getDocs,
    serverTimestamp,
    onSnapshot,
    orderBy,
    limit,
    startAfter,
    runTransaction
} from "firebase/firestore";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import {
    FiLogIn,
    FiLogOut,
    FiUnlock,
    FiSearch,
    FiHome,
    FiLock,
    FiCalendar,
    FiTrendingUp,
    FiActivity,
    FiDollarSign,
    FiLoader,
    FiLayers,
    FiRefreshCw,
    FiX,
    FiUsers,
    FiClock,
    FiTrash2,
    FiUser
} from "react-icons/fi";

registerLocale("mn", mn);

const MySwal = withReactContent(Swal);

/**
 * Firestore Timestamp, Date, string болон number
 * хэлбэрийн хугацааг форматлана.
 */
const formatDateTime = (value) => {
    if (!value) return "-";

    let dateObject;

    if (typeof value?.toDate === "function") {
        dateObject = value.toDate();
    } else if (
        typeof value === "string" ||
        typeof value === "number"
    ) {
        dateObject = new Date(value);
    } else if (value instanceof Date) {
        dateObject = value;
    } else {
        return "-";
    }

    if (Number.isNaN(dateObject.getTime())) {
        return "-";
    }

    const year = dateObject.getFullYear();

    const month = String(
        dateObject.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
        dateObject.getDate()
    ).padStart(2, "0");

    const hour = String(
        dateObject.getHours()
    ).padStart(2, "0");

    const minute = String(
        dateObject.getMinutes()
    ).padStart(2, "0");

    return `${year}.${month}.${day} ${hour}:${minute}`;
};

/**
 * Үнэ string эсвэл number хэлбэртэй байсан ч
 * найдвартай тоо болгон хувиргана.
 */
const getCleanPrice = (value) => {
    if (!value) return 0;

    if (typeof value === "number") {
        return Number.isFinite(value) ? value : 0;
    }

    const parsedValue = Number(
        String(value).replace(/[^0-9]/g, "")
    );

    return Number.isNaN(parsedValue)
        ? 0
        : parsedValue;
};

/**
 * Улсын дугаарыг lookup key болгон нэг хэлбэрт оруулна.
 */
const normalizePlateKey = (value) =>
    String(value || "")
        .trim()
        .toUpperCase()
        .replace(/\s+/g, "");


/**
 * Timestamp/ISO/Date утгыг JS Date болгоно.
 */
const toDateObject = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value?.toDate === "function") {
        const date = value.toDate();
        return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof value?.seconds === "number") {
        const date = new Date(value.seconds * 1000);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof value?._seconds === "number") {
        const date = new Date(value._seconds * 1000);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const isSameLocalDay = (left, right) =>
    left &&
    right &&
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate();

/**
 * Урилгын modal-ийн KPI-г client талд тооцно.
 */
const summarizeInviterHistory = (logs = []) => {
    const uniqueVehicles = new Set();
    const now = new Date();
    let todayCount = 0;
    let parkedCount = 0;

    logs.forEach((log) => {
        const guestPlate = normalizePlateKey(
            log?.car_plate || log?.plate
        );

        if (guestPlate) {
            uniqueVehicles.add(guestPlate);
        }

        const entryDate = toDateObject(log?.entry_time);
        if (entryDate && isSameLocalDay(entryDate, now)) {
            todayCount += 1;
        }

        const status = String(log?.status || "")
            .trim()
            .toLowerCase();

        if (!log?.exit_time || status === "parked") {
            parkedCount += 1;
        }
    });

    return {
        total: logs.length,
        uniqueVehicles: uniqueVehicles.size,
        todayCount,
        parkedCount
    };
};

/**
 * Firebase болон Cloud Function алдааны
 * хэрэглэгчид харуулах мессеж.
 */
const getErrorMessage = (
    error,
    fallbackMessage = "Тодорхойгүй алдаа гарлаа."
) => {
    if (typeof error?.message === "string") {
        return error.message;
    }

    if (typeof error?.details === "string") {
        return error.details;
    }

    return fallbackMessage;
};


const LEGACY_GATE_CAMERAS = [
    {
        camera_id: "enter_1",
        name: "Орох хаалга",
        role: "enter",
        enabled: true,
        configured: true,
        legacyField: "gate.enterOpen"
    },
    {
        camera_id: "exit_1",
        name: "Гарах хаалга",
        role: "exit",
        enabled: true,
        configured: true,
        legacyField: "gate.exitOpen"
    }
];

const normalizeDashboardCameraPlan = (
    rawPlan,
    rawCameras
) => {
    const rawRows = Array.isArray(rawCameras)
        ? rawCameras
        : rawCameras &&
            typeof rawCameras === "object"
          ? Object.entries(rawCameras).map(
                ([key, value]) => ({
                    ...(value &&
                    typeof value === "object"
                        ? value
                        : {}),
                    camera_id:
                        value?.camera_id ||
                        value?.id ||
                        key
                })
            )
          : [];

    const countConfiguredRole = (role) =>
        rawRows.filter((camera) => {
            const cameraRole =
                String(camera?.role || "")
                    .trim()
                    .toLowerCase() === "exit"
                    ? "exit"
                    : "enter";

            return (
                cameraRole === role &&
                camera?.configured !== false
            );
        }).length;

    const actualEnter =
        countConfiguredRole("enter");
    const actualExit =
        countConfiguredRole("exit");

    const hasPlan =
        rawPlan &&
        typeof rawPlan === "object" &&
        !Array.isArray(rawPlan);

    let enterCount = hasPlan
        ? Number(rawPlan.enterCount)
        : actualEnter;

    let exitCount = hasPlan
        ? Number(rawPlan.exitCount)
        : actualExit;

    if (
        !Number.isInteger(enterCount) ||
        enterCount < 0
    ) {
        enterCount = actualEnter;
    }

    if (
        !Number.isInteger(exitCount) ||
        exitCount < 0
    ) {
        exitCount = actualExit;
    }

    enterCount = Math.min(
        32,
        Math.max(0, enterCount)
    );
    exitCount = Math.min(
        32,
        Math.max(0, exitCount)
    );

    if (
        !hasPlan &&
        actualEnter === 0 &&
        actualExit === 0
    ) {
        enterCount = 1;
        exitCount = 1;
    }

    return {
        enterCount,
        exitCount,
        totalCount:
            enterCount + exitCount
    };
};

const normalizeGateDisplayName = (
    value,
    role,
    index
) => {
    const fallback =
        role === "exit"
            ? `Гарах хаалга ${index}`
            : `Орох хаалга ${index}`;

    const raw = String(value || "").trim();
    if (!raw) return fallback;

    return raw
        .replace(/Орох\s+камер/gi, "Орох хаалга")
        .replace(/Гарах\s+камер/gi, "Гарах хаалга")
        .replace(/\bENTER\s+CAMERA\b/gi, "Орох хаалга")
        .replace(/\bEXIT\s+CAMERA\b/gi, "Гарах хаалга");
};

const normalizeGateCameras = (
    rawValue,
    dashboardButtons = {},
    cameraPlan = null
) => {
    const rows = Array.isArray(rawValue)
        ? rawValue
        : rawValue &&
            typeof rawValue === "object"
          ? Object.entries(rawValue).map(
                ([cameraId, value]) => ({
                    ...(value &&
                    typeof value === "object"
                        ? value
                        : {}),
                    camera_id:
                        value?.camera_id ||
                        value?.id ||
                        cameraId
                })
            )
          : [];

    const buttonSettings =
        dashboardButtons &&
        typeof dashboardButtons === "object" &&
        !Array.isArray(dashboardButtons)
            ? dashboardButtons
            : {};

    const hasExplicitCameraPlan =
        cameraPlan &&
        typeof cameraPlan === "object" &&
        !Array.isArray(cameraPlan);

    /*
     * Backward compatibility:
     * Хуучин parking document дээр gate.cameras болон gate.cameraPlan
     * огт байхгүй бол planned placeholder камер үүсгэхгүй.
     *
     * Ингэснээр displayedGateCameras нь LEGACY_GATE_CAMERAS руу fallback
     * хийж gate.enterOpen / gate.exitOpen товчнууд идэвхтэй үлдэнэ.
     *
     * Харин шинэ parking дээр cameraPlan байгаа боловч local registry
     * хараахан publish хийгдээгүй бол placeholder камер disabled хэвээр
     * харагдана — энэ нь шинэ architecture-ийн хамгаалалт.
     */
    if (rows.length === 0 && !hasExplicitCameraPlan) {
        return [];
    }

    const actualById = new Map();

    rows.forEach((camera) => {
        const cameraId = String(
            camera?.camera_id ||
                camera?.id ||
                ""
        )
            .trim()
            .toLowerCase();

        if (!cameraId) return;

        const role =
            String(camera?.role || "")
                .trim()
                .toLowerCase() === "exit"
                ? "exit"
                : "enter";

        actualById.set(cameraId, {
            camera_id: cameraId,
            name: normalizeGateDisplayName(
                camera?.name,
                role,
                Number(
                    String(cameraId).split("_").pop()
                ) || 1
            ),
            role,
            vendor: String(
                camera?.vendor || ""
            )
                .trim()
                .toLowerCase(),
            enabled:
                camera?.enabled !== false,
            configured:
                camera?.configured !== false
        });
    });

    const plan = normalizeDashboardCameraPlan(
        cameraPlan,
        rawValue
    );

    const plannedIds = [];

    for (
        let index = 1;
        index <= plan.enterCount;
        index += 1
    ) {
        plannedIds.push({
            cameraId: `enter_${index}`,
            role: "enter",
            index
        });
    }

    for (
        let index = 1;
        index <= plan.exitCount;
        index += 1
    ) {
        plannedIds.push({
            cameraId: `exit_${index}`,
            role: "exit",
            index
        });
    }

    return plannedIds.map(
        (
            {
                cameraId,
                role,
                index
            },
            plannedIndex
        ) => {
            const actual =
                actualById.get(cameraId);

            const button =
                buttonSettings[cameraId] ||
                {};

            return {
                ...(actual || {}),
                camera_id: cameraId,
                role,
                name: normalizeGateDisplayName(
                    actual?.name,
                    role,
                    index
                ),
                vendor:
                    actual?.vendor || "",
                enabled:
                    actual?.enabled === true,
                configured:
                    Boolean(actual) &&
                    actual?.configured !== false,
                dashboardLabel:
                    normalizeGateDisplayName(
                        button?.label || actual?.name,
                        role,
                        index
                    ),
                dashboardVisible:
                    button?.visible !== false,
                dashboardOrder:
                    Number.isFinite(
                        Number(button?.order)
                    )
                        ? Math.max(
                              0,
                              Math.trunc(
                                  Number(
                                      button.order
                                  )
                              )
                          )
                        : plannedIndex + 1
            };
        }
    );
};

const createGateCommandId = () =>
    `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;

const GATE_COMMAND_TIMEOUT_MS = 45000;

const normalizeCommandStatus = (value) =>
    String(value || "")
        .trim()
        .toUpperCase();

const timestampKey = (value) => {
    if (!value) return "";

    if (
        typeof value?.seconds === "number" &&
        typeof value?.nanoseconds === "number"
    ) {
        return `${value.seconds}:${value.nanoseconds}`;
    }

    if (typeof value?.toMillis === "function") {
        return String(value.toMillis());
    }

    if (value instanceof Date) {
        return String(value.getTime());
    }

    return String(value);
};

const waitForDynamicGateCommand = (
    parkingReference,
    cameraId,
    requestId,
    timeoutMs = GATE_COMMAND_TIMEOUT_MS
) =>
    new Promise((resolve, reject) => {
        let settled = false;
        let unsubscribe = () => {};

        const finish = (callback, value) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);

            try {
                unsubscribe();
            } catch (_) {}

            callback(value);
        };

        const timer = setTimeout(() => {
            const error = new Error(
                "Сервертэй холбогдсон боловч хаалтаас команд хүлээж авсан хариу ирсэнгүй"
            );
            error.code = "gate/server-timeout";
            finish(reject, error);
        }, timeoutMs);

        unsubscribe = onSnapshot(
            parkingReference,
            (snapshot) => {
                if (!snapshot.exists()) return;

                const data = snapshot.data() || {};
                const command =
                    data.gate?.commands?.[cameraId];

                if (!command) return;

                if (
                    String(command.requestId || "") !==
                    String(requestId || "")
                ) {
                    return;
                }

                const status =
                    normalizeCommandStatus(
                        command.status
                    );

                if (status === "SUCCESS") {
                    finish(resolve, {
                        status,
                        message:
                            command.message ||
                            "Сервер хаалт нээх командыг амжилттай гүйцэтгэлээ.",
                        attempt:
                            Number(command.attempt) || 1
                    });
                    return;
                }

                if (
                    [
                        "FAILED",
                        "SCHEDULE_FAILED",
                        "CANCELLED",
                        "CONFLICT"
                    ].includes(status)
                ) {
                    const error = new Error(
                        command.message ||
                            `Хаалт нээх команд ${status} төлөвтэй дууслаа.`
                    );
                    error.code =
                        `gate/${status.toLowerCase()}`;
                    error.commandStatus = status;
                    error.attempt =
                        Number(command.attempt) || 0;
                    finish(reject, error);
                }
            },
            (snapshotError) => {
                const error = new Error(
                    getErrorMessage(
                        snapshotError,
                        "Хаалганы command status уншиж чадсангүй."
                    )
                );
                error.code = "gate/status-listener-error";
                finish(reject, error);
            }
        );
    });

const waitForLegacyGateCommand = (
    parkingReference,
    commandId,
    previousUpdatedAtKey = "",
    timeoutMs = GATE_COMMAND_TIMEOUT_MS
) =>
    new Promise((resolve, reject) => {
        let settled = false;
        let unsubscribe = () => {};

        const finish = (callback, value) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);

            try {
                unsubscribe();
            } catch (_) {}

            callback(value);
        };

        const timer = setTimeout(() => {
            const error = new Error(
                "Команд хадгалагдсан боловч хаалт хугацаандаа хариу өгсөнгүй."
            );
            error.code = "gate/server-timeout";
            finish(reject, error);
        }, timeoutMs);

        unsubscribe = onSnapshot(
            parkingReference,
            (snapshot) => {
                if (!snapshot.exists()) return;

                const data = snapshot.data() || {};
                const commandStatus =
                    data.remoteCommandStatus?.[
                        commandId
                    ];

                if (!commandStatus) return;

                const updatedAtKey =
                    timestampKey(
                        commandStatus.updatedAt
                    );

                // Old SUCCESS/FAILED status-ыг шинэ command-ийн үр дүн гэж
                // андуурахгүй. Server шинэ status бичсэн үед updatedAt өөрчлөгдөнө.
                if (
                    previousUpdatedAtKey &&
                    updatedAtKey ===
                        previousUpdatedAtKey
                ) {
                    return;
                }

                const status =
                    normalizeCommandStatus(
                        commandStatus.status
                    );

                if (status === "SUCCESS") {
                    finish(resolve, {
                        status,
                        message:
                            commandStatus.message ||
                            "Сервер хаалт нээх командыг амжилттай гүйцэтгэлээ.",
                        attempt:
                            Number(
                                commandStatus.attempt
                            ) || 1
                    });
                    return;
                }

                if (
                    [
                        "FAILED",
                        "SCHEDULE_FAILED",
                        "CANCELLED",
                        "CONFLICT"
                    ].includes(status)
                ) {
                    const error = new Error(
                        commandStatus.message ||
                            `Хаалт нээх command ${status} төлөвтэй дууслаа.`
                    );
                    error.code =
                        `gate/${status.toLowerCase()}`;
                    error.commandStatus = status;
                    error.attempt =
                        Number(
                            commandStatus.attempt
                        ) || 0;
                    finish(reject, error);
                }
            },
            (snapshotError) => {
                const error = new Error(
                    getErrorMessage(
                        snapshotError,
                        "Legacy gate command status уншиж чадсангүй."
                    )
                );
                error.code = "gate/status-listener-error";
                finish(reject, error);
            }
        );
    });

/**
 * KPI үзүүлэлтийн карт.
 */
const StatCard = ({
    label,
    value,
    icon: Icon,
    colorClass,
    bgClass
}) => {
    return (
        <div className="tw-bg-white tw-p-5 sm:tw-p-6 tw-rounded-2xl tw-border tw-border-slate-100 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-300">
            <div className="tw-flex tw-items-center tw-gap-4">
                <div
                    className={`
                        tw-w-12
                        tw-h-12
                        tw-rounded-xl
                        ${bgClass}
                        tw-flex
                        tw-items-center
                        tw-justify-center
                        tw-shrink-0
                    `}
                >
                    <Icon
                        className={`
                            tw-w-6
                            tw-h-6
                            ${colorClass}
                        `}
                    />
                </div>

                <div>
                    <p className="tw-text-xs tw-font-normal tw-text-slate-500 tw-uppercase tw-tracking-wider">
                        {label}
                    </p>

                    <h3 className="tw-text-xl sm:tw-text-2xl tw-font-bold tw-text-slate-900 tw-mt-1">
                        {value}
                    </h3>
                </div>
            </div>
        </div>
    );
};

/**
 * Хаалга нээх товч.
 */
const GateButton = ({
    onClick,
    icon: Icon,
    label,
    gradientClass,
    shadowClass,
    disabled = false
}) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleClick = async () => {
        if (isLoading || disabled) return;

        setIsLoading(true);

        try {
            await onClick();
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={isLoading || disabled}
            className={`
                tw-group
                tw-relative
                tw-w-full
                tw-min-w-0
                tw-flex
                tw-items-center
                tw-justify-center
                tw-gap-2
                sm:tw-gap-2.5
                ${gradientClass}
                tw-text-white
                tw-px-3
                sm:tw-px-4
                xl:tw-px-5
                tw-py-3
                sm:tw-py-4
                tw-rounded-xl
                sm:tw-rounded-2xl
                tw-font-normal
                tw-text-xs
                sm:tw-text-sm
                xl:tw-text-base
                ${shadowClass}
                active:tw-scale-[0.98]
                tw-transition-all
                tw-duration-300
                tw-overflow-hidden
                disabled:tw-opacity-60
                disabled:tw-cursor-not-allowed
            `}
        >
            <div className="tw-absolute tw-inset-0 tw-bg-white/20 tw-translate-y-full group-hover:tw-translate-y-0 tw-transition-transform tw-duration-300 tw-ease-out" />

            {isLoading ? (
                <FiLoader className="tw-relative tw-z-10 tw-w-4 tw-h-4 sm:tw-w-5 sm:tw-h-5 tw-animate-spin tw-shrink-0" />
            ) : (
                <>
                    <Icon className="tw-relative tw-z-10 tw-w-4 tw-h-4 sm:tw-w-5 sm:tw-h-5 tw-shrink-0" />

                    <span className="tw-relative tw-z-10 tw-tracking-wide tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis">
                        {label}
                    </span>

                    <FiUnlock className="tw-relative tw-z-10 tw-w-4 tw-h-4 sm:tw-w-5 sm:tw-h-5 tw-shrink-0 tw-opacity-70 group-hover:tw-opacity-100" />
                </>
            )}
        </button>
    );
};

export default function Dashboard() {
    const pageSize = 50;

    const [parkingLogs, setParkingLogs] = useState([]);

    const [
        totalEnteredCount,
        setTotalEnteredCount
    ] = useState(0);

    const [
        occupiedSpots,
        setOccupiedSpots
    ] = useState(0);

    const [
        totalRevenue,
        setTotalRevenue
    ] = useState(0);

    const [
        totalLoss,
        setTotalLoss
    ] = useState(0);

    const [
        isAggregating,
        setIsAggregating
    ] = useState(false);

    const [
        searchInput,
        setSearchInput
    ] = useState("");

    const [
        searchPlate,
        setSearchPlate
    ] = useState("");

    const [
        selectedDate,
        setSelectedDate
    ] = useState(new Date());

    const [
        paymentFilter,
        setPaymentFilter
    ] = useState("all");

    const [
        userRole,
        setUserRole
    ] = useState("operator");

    const [
        isLoadingData,
        setIsLoadingData
    ] = useState(true);

    const [
        pageCursors,
        setPageCursors
    ] = useState([]);

    const [
        currentPage,
        setCurrentPage
    ] = useState(0);

    const [
        hasNextPage,
        setHasNextPage
    ] = useState(true);

    const [
        userReady,
        setUserReady
    ] = useState(false);

    const [
        parkId,
        setParkId
    ] = useState("");

    const [
        parkingName,
        setParkingName
    ] = useState("");

    const [
        gateCameras,
        setGateCameras
    ] = useState([]);

    /**
     * Manual refresh үед өөрчлөгдөж, KPI болон
     * хүснэгтийн useEffect-үүдийг дахин ажиллуулна.
     */
    const [refreshKey, setRefreshKey] = useState(0);

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState(false);

    // Түүхийг хугацаагаар сонгон аюулгүй бөөнөөр устгах.
    const [showBulkDelete, setShowBulkDelete] = useState(false);
    const [bulkStartDate, setBulkStartDate] = useState(null);
    const [bulkEndDate, setBulkEndDate] = useState(null);
    const [bulkWorking, setBulkWorking] = useState(false);
    const [bulkProgress, setBulkProgress] = useState("");
    // Хуучин дууссан эсэх нь тодорхойгүй (exit_time байхгүй гэх мэт)
    // түүхийг зөвхөн хэрэглэгч өөрөө сонгосон үед нэмэлтээр устгана.
    const [bulkIncludeProtected, setBulkIncludeProtected] = useState(false);

    // Энэ системд master нь superadmin эрхтэй дүйцнэ.
    // explicit "superadmin" role-г мөн танина. Бусад role-д бүхэл panel нуугдана.
    const canBulkDelete = userRole === "master" || userRole === "superadmin";
    // Employee нь өдрийн орлого/алдагдлын KPI-г үзэхгүй.
    // Role баталгаажаагүй үед санхүүгийн картуудыг харуулахгүй.
    const canViewDailyFinance = userReady && userRole !== "employee";
    useEffect(() => {
        if (!canBulkDelete) {
            setShowBulkDelete(false);
            setBulkIncludeProtected(false);
        }
    }, [canBulkDelete]);

    const [inviterHistory, setInviterHistory] = useState({
        open: false,
        loading: false,
        inviter: null,
        logs: [],
        startTimeISO: "",
        endTimeISO: "",
        error: "",
        truncated: false
    });

    const auth = getAuth();
    const db = getFirestore(auth.app);

    // Бүх callable function энэ төслийн default region (us-central1) дээр ажиллана.
    // getDashboardStats, searchParkingLogs, getInviterHistory бүгд нэг instance ашиглана.
    const functions = getFunctions(auth.app);

    /**
     * Урьсан хүний мэдээллийг parking record-оос шууд уншина.
     * registeredCars руу нэмэлт lookup/query хийхгүй.
     */

    /**
     * parkings/{parkId} document-оос нэр уншина.
     *
     * Дараах бүх бүтэц ажиллана:
     *
     * {
     *     name: "Зогсоолын нэр"
     * }
     *
     * {
     *     config: {
     *         name: "Зогсоолын нэр"
     *     }
     * }
     *
     * {
     *     settings: {
     *         name: "Зогсоолын нэр"
     *     }
     * }
     */
    const fetchParkingName = useCallback(
        async (currentParkId) => {
            if (!currentParkId) {
                setParkingName("");
                return;
            }

            try {
                const parkingReference = doc(
                    db,
                    "parkings",
                    currentParkId
                );

                const parkingSnapshot = await getDoc(
                    parkingReference
                );

                if (!parkingSnapshot.exists()) {
                    console.warn(
                        `Зогсоолын document олдсонгүй: parkings/${currentParkId}`
                    );

                    setParkingName(currentParkId);
                    setGateCameras([]);
                    return;
                }

                const parkingData =
                    parkingSnapshot.data() || {};

                setGateCameras(
                    normalizeGateCameras(
                        parkingData.gate?.cameras,
                        parkingData.gate?.dashboardButtons,
                        parkingData.gate?.cameraPlan
                    )
                );

                const rawName =
                    parkingData.name ||
                    parkingData.config?.name ||
                    parkingData.settings?.name ||
                    parkingData.parkingName ||
                    currentParkId;

                const safeName = String(
                    rawName || ""
                ).trim();

                setParkingName(
                    safeName || currentParkId
                );
            } catch (error) {
                console.error(
                    "Зогсоолын нэр уншихад алдаа гарлаа:",
                    error
                );

                setParkingName(currentParkId);
                setGateCameras([]);
            }
        },
        [db]
    );

    /**
     * Нэвтэрсэн хэрэглэгчийн role уншина.
     */
    const fetchUserRole = useCallback(
        async (user, currentParkId) => {
            if (!user?.email || !currentParkId) {
                setUserRole("operator");
                return;
            }

            try {
                const userReference = doc(
                    db,
                    "parkings",
                    currentParkId,
                    "users",
                    user.email
                );

                const userSnapshot = await getDoc(
                    userReference
                );

                if (!userSnapshot.exists()) {
                    setUserRole("operator");
                    return;
                }

                const role = String(
                    userSnapshot.data()?.role || ""
                ).trim().toLowerCase();

                if (role === "master" || role === "superadmin") {
                    setUserRole(role);
                    return;
                }

                if (role === "admin") {
                    setUserRole("admin");
                    return;
                }

                // Legacy typo "emloyee"-г мөн employee гэж танина.
                if (role === "employee" || role === "emloyee") {
                    setUserRole("employee");
                    return;
                }

                setUserRole("operator");
            } catch (error) {
                console.error(
                    "Хэрэглэгчийн эрх уншихад алдаа:",
                    error
                );

                setUserRole("operator");
            }
        },
        [db]
    );

    /**
     * Firebase Authentication хэрэглэгчийг сонсоно.
     *
     * Энэ системд:
     * user.displayName = parkId
     */
    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(
            auth,
            async (user) => {
                if (!user) {
                    setParkId("");
                    setParkingName("");
                    setGateCameras([]);
                    setUserRole("operator");
                    setUserReady(false);
                    setParkingLogs([]);
                    setPageCursors([]);
                    setCurrentPage(0);
                    setHasNextPage(false);
                    setIsLoadingData(false);

                    return;
                }

                setUserReady(false);

                try {
                    await user.getIdToken(true);

                    const currentParkId = String(
                        user.displayName || ""
                    ).trim();

                    if (!currentParkId) {
                        throw new Error(
                            "Firebase Auth хэрэглэгчийн displayName буюу parkId тохируулагдаагүй байна."
                        );
                    }

                    setParkId(currentParkId);

                    await Promise.all([
                        fetchParkingName(currentParkId),
                        fetchUserRole(
                            user,
                            currentParkId
                        )
                    ]);

                    setUserReady(true);
                } catch (error) {
                    console.error(
                        "Firebase хэрэглэгчийн мэдээлэл бэлтгэхэд алдаа:",
                        error
                    );

                    setParkId("");
                    setParkingName("");
                    setUserReady(false);
                    setIsLoadingData(false);

                    await MySwal.fire({
                        icon: "error",
                        title: "Тохиргооны алдаа",
                        text: getErrorMessage(
                            error,
                            "Firebase хэрэглэгчийн тохиргоог шалгана уу."
                        ),
                        confirmButtonColor: "#ef4444"
                    });
                }
            }
        );

        return () => {
            unsubscribeAuth();
        };
    }, [
        auth,
        fetchParkingName,
        fetchUserRole
    ]);

    /**
     * Хайлтын debounce.
     */
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchPlate(
                searchInput
                    .trim()
                    .toUpperCase()
            );
        }, 500);

        return () => {
            clearTimeout(timer);
        };
    }, [searchInput]);

    /**
     * Системээс гарах.
     */
    const handleLogout = async () => {
        try {
            await signOut(auth);
        } catch (error) {
            await MySwal.fire({
                icon: "error",
                title: "Алдаа",
                text: getErrorMessage(
                    error,
                    "Системээс гарахад алдаа гарлаа."
                ),
                confirmButtonColor: "#ef4444"
            });
        }
    };

    /**
     * Сонгосон өдрийн нийт статистик татах.
     */
    useEffect(() => {
        if (!userReady || !parkId) {
            return undefined;
        }

        let isMounted = true;

        const loadAggregates = async () => {
            setIsAggregating(true);

            const startTime =
                new Date(selectedDate);

            startTime.setHours(
                0,
                0,
                0,
                0
            );

            const endTime =
                new Date(selectedDate);

            endTime.setHours(
                23,
                59,
                59,
                999
            );

            try {
                const getDashboardStats =
                    httpsCallable(
                        functions,
                        "getDashboardStats"
                    );

                const response =
                    await getDashboardStats({
                        parkId,
                        startTimeISO:
                            startTime.toISOString(),
                        endTimeISO:
                            endTime.toISOString()
                    });

                if (!isMounted) return;

                const data =
                    response.data || {};

                setTotalEnteredCount(
                    Number(data.totalEntered) || 0
                );

                setOccupiedSpots(
                    Number(data.occupied) || 0
                );

                // Employee-д санхүүгийн дүнг React state-д хадгалахгүй.
                // Backend талд мөн role-based хандалтыг хязгаарлах шаардлагатай.
                setTotalRevenue(
                    userRole === "employee" ? 0 : Number(data.revenue) || 0
                );

                setTotalLoss(
                    userRole === "employee" ? 0 : Number(data.loss) || 0
                );
            } catch (error) {
                console.error(
                    "Статистик татахад алдаа:",
                    error
                );

                if (isMounted) {
                    setTotalEnteredCount(0);
                    setOccupiedSpots(0);
                    setTotalRevenue(0);
                    setTotalLoss(0);
                }
            } finally {
                if (isMounted) {
                    setIsAggregating(false);
                }
            }
        };

        loadAggregates();

        return () => {
            isMounted = false;
        };
    }, [
        selectedDate,
        userReady,
        parkId,
        functions,
        refreshKey,
        userRole
    ]);

    /**
     * Эхний хуудасны зогсоолын бүртгэл татах.
     */
    useEffect(() => {
        if (!userReady || !parkId) {
            return undefined;
        }

        let isMounted = true;

        const loadParkingLogs = async () => {
            setIsLoadingData(true);

            const startTime =
                new Date(selectedDate);

            startTime.setHours(
                0,
                0,
                0,
                0
            );

            const endTime =
                new Date(selectedDate);

            endTime.setHours(
                23,
                59,
                59,
                999
            );

            try {
                const searchParkingLogs =
                    httpsCallable(
                        functions,
                        "searchParkingLogs"
                    );

                const response =
                    await searchParkingLogs({
                        parkId,
                        startTimeISO:
                            startTime.toISOString(),
                        endTimeISO:
                            endTime.toISOString(),
                        paymentFilter,
                        searchPlate,
                        limitSize: pageSize,
                        lastDocId: null
                    });

                if (!isMounted) return;

                const logsFromBackend =
                    Array.isArray(
                        response.data?.logs
                    )
                        ? response.data.logs
                        : [];

                setParkingLogs(
                    logsFromBackend
                );

                setHasNextPage(
                    Boolean(
                        response.data?.hasNextPage
                    )
                );

                setCurrentPage(0);

                if (
                    logsFromBackend.length > 0
                ) {
                    const lastLog =
                        logsFromBackend[
                            logsFromBackend.length - 1
                        ];

                    setPageCursors(
                        lastLog?.id
                            ? [lastLog.id]
                            : []
                    );
                } else {
                    setPageCursors([]);
                }
            } catch (error) {
                console.error(
                    "Жагсаалт татахад алдаа:",
                    error
                );

                if (isMounted) {
                    setParkingLogs([]);
                    setPageCursors([]);
                    setHasNextPage(false);
                    setCurrentPage(0);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingData(false);
                    setIsRefreshing(false);
                }
            }
        };

        loadParkingLogs();

        return () => {
            isMounted = false;
        };
    }, [
        selectedDate,
        paymentFilter,
        searchPlate,
        userReady,
        parkId,
        functions,
        refreshKey
    ]);

    /**
     * Pagination хуудас татах.
     */
    const fetchPage = async (pageIndex) => {
        if (
            !parkId ||
            isLoadingData ||
            pageIndex < 0
        ) {
            return;
        }

        setIsLoadingData(true);

        const startTime =
            new Date(selectedDate);

        startTime.setHours(
            0,
            0,
            0,
            0
        );

        const endTime =
            new Date(selectedDate);

        endTime.setHours(
            23,
            59,
            59,
            999
        );

        const lastDocId =
            pageIndex > 0
                ? pageCursors[
                    pageIndex - 1
                ] || null
                : null;

        try {
            const searchParkingLogs =
                httpsCallable(
                    functions,
                    "searchParkingLogs"
                );

            const response =
                await searchParkingLogs({
                    parkId,
                    startTimeISO:
                        startTime.toISOString(),
                    endTimeISO:
                        endTime.toISOString(),
                    paymentFilter,
                    searchPlate,
                    limitSize: pageSize,
                    lastDocId
                });

            const logsFromBackend =
                Array.isArray(
                    response.data?.logs
                )
                    ? response.data.logs
                    : [];

            setParkingLogs(
                logsFromBackend
            );

            setHasNextPage(
                Boolean(
                    response.data?.hasNextPage
                )
            );

            setCurrentPage(pageIndex);

            if (
                logsFromBackend.length > 0
            ) {
                const lastLog =
                    logsFromBackend[
                        logsFromBackend.length - 1
                    ];

                if (lastLog?.id) {
                    setPageCursors(
                        (previousCursors) => {
                            const updatedCursors = [
                                ...previousCursors
                            ];

                            updatedCursors[
                                pageIndex
                            ] = lastLog.id;

                            return updatedCursors;
                        }
                    );
                }
            }
        } catch (error) {
            console.error(
                "Хуудас шилжихэд алдаа:",
                error
            );

            await MySwal.fire({
                icon: "error",
                title: "Алдаа",
                text: getErrorMessage(
                    error,
                    "Хуудасны мэдээлэл татаж чадсангүй."
                ),
                confirmButtonColor: "#ef4444"
            });
        } finally {
            setIsLoadingData(false);
        }
    };

    const handleNext = () => {
        if (
            hasNextPage &&
            !isLoadingData
        ) {
            fetchPage(
                currentPage + 1
            );
        }
    };

    const handlePrevious = () => {
        if (
            currentPage > 0 &&
            !isLoadingData
        ) {
            fetchPage(
                currentPage - 1
            );
        }
    };

    /**
     * KPI болон эхний хуудсын мэдээллийг гараар шинэчилнэ.
     * Realtime listener ашиглахгүй тул idle үед нэмэлт read гарахгүй.
     */
    const handleRefresh = () => {
        if (
            isLoadingData ||
            isAggregating ||
            isRefreshing ||
            !userReady ||
            !parkId
        ) {
            return;
        }

        setIsRefreshing(true);

        setRefreshKey(
            (previousValue) => previousValue + 1
        );
    };


    /**
     * Хугацаагаар Firebase records-ийг клиент талаас бөөнөөр устгана.
     * Cloud Function НЭМЭХГҮЙ. Одоогийн бусад callable-ууд хэвээр.
     *
     * Firestore Security Rules-д зөвхөн master/superadmin-д records DELETE
     * зөвшөөрөл тусад нь тохируулсан байх ЁСТОЙ. UI role нь хамгаалалт биш.
     * Устгалын өмнө transaction бүр дотор record-ийг дахин уншиж
     * статус/огноог шалгана. Сүүлийн 24 цагийн түүхийг ямар ч
     * тохиргоонд устгахгүй. Хуучин боловч нээлттэй гэж үзэгдсэн
     * түүхийг зөвхөн тусгай сонголт, хоёр дахь баталгаажуулалтаар устгана.
     */
    const handleBulkDeleteRecords = async () => {
        if (bulkWorking || !userReady || !parkId) {
            return;
        }
        if (!canBulkDelete) {
            await MySwal.fire({
                icon: "warning",
                title: "Устгах эрх хүрэхгүй байна",
                text: "Бүртгэлийг зөвхөн superadmin эрхтэй хэрэглэгч устгаж болно.",
                confirmButtonColor: "#dc2626"
            });
            return;
        }

        const dayKey = (value) => {
            if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
                return "";
            }
            const year = value.getFullYear();
            const month = String(value.getMonth() + 1).padStart(2, "0");
            const day = String(value.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
        };
        const startDay = dayKey(bulkStartDate);
        const endDay = dayKey(bulkEndDate);
        const todayDay = new Date(Date.now() + 8 * 60 * 60 * 1000)
            .toISOString().slice(0, 10);
        const startTime = new Date(`${startDay}T00:00:00.000+08:00`);
        const endTime = new Date(`${endDay}T23:59:59.999+08:00`);

        if (
            !startDay || !endDay ||
            Number.isNaN(startTime.getTime()) ||
            Number.isNaN(endTime.getTime()) ||
            startTime > endTime || endDay >= todayDay ||
            endTime.getTime() - startTime.getTime() > 366 * 86400000
        ) {
            await MySwal.fire({
                icon: "warning",
                title: "Хугацаа буруу байна",
                text: "Өнөөдрөөс өмнөх 366 хүртэл хоногийн зөв интервал сонгоно уу."
            });
            return;
        }

        const startLabel = startDay;
        const endLabel = endDay;
        const recordsRef = collection(db, "parkings", parkId, "records");
        const PAGE_SIZE = 100;
        const TRANSACTION_SIZE = 40;
        const MAX_PAGES = 2000;
        const safeThresholdMs = Date.now() - 24 * 60 * 60 * 1000;

        // Яагаад хамгаалагдсаныг нэг бүрчлэн тодорхойлж, UI дээр харуулна.
        // safe === true бол ердийн устгалд хамрагдана.
        // forced === true бол зөвхөн bulkIncludeProtected сонгосон үед хамрагдана.
        const classifyRecord = (record, minEntryMs) => {
            const entry = toDateObject(record?.entry_time);
            if (!entry || entry < startTime || entry > endTime) {
                return { eligible: false, forced: false, reason: "Орсон хугацаа хүчингүй" };
            }
            if (entry.getTime() >= minEntryMs) {
                return { eligible: false, forced: false, reason: "Сүүлийн 24 цагийн бүртгэл" };
            }

            const exit = toDateObject(record?.exit_time);
            const status = String(record?.status || "").trim().toLowerCase();
            const protectedStatuses = [
                "parked", "parking", "entered", "active", "pending",
                "payment_pending", "processing"
            ];
            const activeStatus = protectedStatuses.includes(status) ||
                status.includes("тооцоолж") ||
                status.includes("зогсоолд") ||
                status.includes("хүлээн");
            const reasons = [];
            if (!exit) reasons.push("Гарсан хугацаа (exit_time) алга");
            if (activeStatus) reasons.push(`Идэвхтэй төлөв (${status})`);
            const isProtected = reasons.length > 0;

            if (isProtected && !bulkIncludeProtected) {
                return { eligible: false, forced: false, reason: reasons.join(", ") };
            }
            return {
                eligible: true,
                forced: isProtected,
                reason: reasons.join(", ")
            };
        };

        const mayBeUnpaid = (record) => {
            const raw = record?.price;
            const amount = typeof raw === "number"
                ? raw : Number(String(raw || "0").replace(/[^0-9]/g, "")) || 0;
            const paid = record?.payment === true ||
                record?.payment === "Төлөгдсөн" ||
                record?.paymentStatus === true ||
                record?.paymentStatus === "Төлөгдсөн";
            return amount > 0 && !paid && record?.registered !== true;
        };

        // Browser UI-ийн master/superadmin state нь зөвшөөрөл БИШ. Firestore Rules ч мөн
        // адил parkId / role-г давхар шалгах шаардлагатай.
        const checkCurrentManager = async () => {
            const currentUser = auth.currentUser;
            if (
                !currentUser?.email ||
                String(currentUser.displayName || "").trim() !== parkId
            ) {
                throw new Error("Нэвтэрсэн хэрэглэгчийн зогсоол таарахгүй байна.");
            }
            const roleDoc = await getDoc(doc(db, "parkings", parkId, "users", currentUser.email));
            if (!roleDoc.exists() || !["master", "superadmin"].includes(roleDoc.data()?.role)) {
                throw new Error("Зөвхөн superadmin эрхтэй хэрэглэгч устгах боломжтой.");
            }
        };

        const makeQuery = (lastDocument) => query(
            recordsRef,
            where("entry_time", ">=", startTime),
            where("entry_time", "<=", endTime),
            orderBy("entry_time", "asc"),
            ...(lastDocument ? [startAfter(lastDocument)] : []),
            limit(PAGE_SIZE)
        );

        let deletedCount = 0;
        setBulkWorking(true);
        try {
            await checkCurrentManager();
            let previewCursor = null;
            let eligibleCount = 0;
            let protectedCount = 0;
            let forcedCount = 0;
            let unpaidPossible = 0;
            let scanned = 0;
            const protectedReasons = new Map();
            const protectedSamples = [];
            let previewFinished = false;

            // Preview: хүснэгтийн эхний 50 мөрөөс үл хамааран бүх хуудсыг уншина.
            for (let page = 0; page < MAX_PAGES; page += 1) {
                setBulkProgress(`Бүртгэл шалгаж байна... ${scanned.toLocaleString()} мөр`);
                const pageSnapshot = await getDocs(makeQuery(previewCursor));
                if (pageSnapshot.empty) {
                    previewFinished = true;
                    break;
                }
                for (const recordDoc of pageSnapshot.docs) {
                    scanned += 1;
                    const record = recordDoc.data();
                    const classification = classifyRecord(record, safeThresholdMs);
                    if (classification.eligible) {
                        eligibleCount += 1;
                        if (classification.forced) forcedCount += 1;
                        if (mayBeUnpaid(record)) unpaidPossible += 1;
                    } else {
                        protectedCount += 1;
                        const why = classification.reason || "Тодорхойгүй";
                        protectedReasons.set(why, (protectedReasons.get(why) || 0) + 1);
                        if (protectedSamples.length < 5) {
                            protectedSamples.push(
                                `${String(record.car_plate || record.plate || recordDoc.id)} — ${why}`
                            );
                        }
                    }
                }
                previewCursor = pageSnapshot.docs[pageSnapshot.docs.length - 1];
                if (pageSnapshot.size < PAGE_SIZE) {
                    previewFinished = true;
                    break;
                }
            }
            if (!previewFinished) {
                throw new Error("Хэт олон бүртгэл байна. Илүү богино хугацаа сонгоно уу.");
            }
            if (eligibleCount === 0) {
                const details = protectedSamples.length
                    ? `\nШалтгаан:\n${protectedSamples.join("\n")}`
                    : "";
                await MySwal.fire({
                    icon: "info",
                    title: "Устгах бүртгэл алга",
                    text: `${startLabel} – ${endLabel}: хамгаалагдсан ${protectedCount.toLocaleString()} бүртгэл байна.${details}${protectedCount > 0 && !bulkIncludeProtected ? "\n\nХэрэв хуучин түүх мөн гэдгийг нягталсан бол доорх 'Хамгаалагдсан хуучин бүртгэлийг мөн устгах' сонголтыг идэвхжүүлээд дахин шалгана уу." : ""}`
                });
                return;
            }

            const confirmPhrase = forcedCount > 0 ? "БҮГДИЙГ УСТГАХ" : "УСТГАХ";
            const confirmation = await MySwal.fire({
                icon: "warning",
                title: "Бүртгэлийг бөөнөөр устгах уу?",
                text: `${startLabel} – ${endLabel} | Устгах: ${eligibleCount.toLocaleString()} | Үүнээс анх хамгаалагдсан: ${forcedCount.toLocaleString()} | Үлдэх хамгаалагдсан: ${protectedCount.toLocaleString()} | Төлөгдөөгүй байж болзошгүй: ${unpaidPossible.toLocaleString()}. ${forcedCount > 0 ? "АНХААР: Гараагүй машин эсвэл төлөгдөөгүй тооцоо устаж болзошгүй! " : ""}Энэ нь буцаах боломжгүй. ${confirmPhrase} гэж бичнэ үү.`,
                input: "text",
                inputPlaceholder: confirmPhrase,
                inputValidator: (value) =>
                    String(value || "").trim() === confirmPhrase
                        ? undefined
                        : `Батлахын тулд ${confirmPhrase} гэж бичнэ үү.`,
                showCancelButton: true,
                confirmButtonText: "Бөөнөөр устгах",
                cancelButtonText: "Буцах",
                confirmButtonColor: "#dc2626",
                reverseButtons: true,
                allowOutsideClick: false
            });
            if (!confirmation.isConfirmed) return;

            // Хуучин, нээлттэй мэт бүртгэлийг албадан устгах бол хоёр дахиа асууна.
            if (forcedCount > 0) {
                const secondConfirm = await MySwal.fire({
                    icon: "error",
                    title: "Сүүлийн анхааруулга",
                    text: `Хамгаалагдсан ${forcedCount} бүртгэлийг мөн устгах гэж байна. Эдгээр нь үнэхээр гарсан эсэх нь баталгаагүй бөгөөд төлбөр, нээлттэй зогсолтод нөлөөлж болно. Үргэлжлүүлэх үү?`,
                    showCancelButton: true,
                    confirmButtonText: "Тийм, эрсдэлийг ойлгосон",
                    cancelButtonText: "Болих",
                    confirmButtonColor: "#b91c1c",
                    allowOutsideClick: false
                });
                if (!secondConfirm.isConfirmed) return;
            }

            await checkCurrentManager();
            let deleteCursor = null;
            let deleteFinished = false;
            for (let page = 0; page < MAX_PAGES; page += 1) {
                setBulkProgress(`Устгаж байна... ${deletedCount.toLocaleString()} / ${eligibleCount.toLocaleString()}`);
                const pageSnapshot = await getDocs(makeQuery(deleteCursor));
                if (pageSnapshot.empty) {
                    deleteFinished = true;
                    break;
                }
                // Сүүлчийн snapshot-ийг хадгална: устгасан ч query cursor зөв явна.
                deleteCursor = pageSnapshot.docs[pageSnapshot.docs.length - 1];
                const candidates = pageSnapshot.docs.filter(
                    (row) => classifyRecord(row.data(), safeThresholdMs).eligible
                );
                for (let offset = 0; offset < candidates.length; offset += TRANSACTION_SIZE) {
                    const group = candidates.slice(offset, offset + TRANSACTION_SIZE);
                    // Firebase client transaction: бүх уншилт эхэлж, дараа нь
                    // баталгаажсан бүртгэлийг л устгана. Concurrent update үед retry.
                    const deletedInTransaction = await runTransaction(
                        db,
                        async (transaction) => {
                            const latestSnapshots = await Promise.all(
                                group.map((row) => transaction.get(row.ref))
                            );
                            let deleteCount = 0;
                            const liveThreshold = Date.now() - 24 * 60 * 60 * 1000;
                            for (const latest of latestSnapshots) {
                                if (
                                    latest.exists() &&
                                    classifyRecord(latest.data(), liveThreshold).eligible
                                ) {
                                    transaction.delete(latest.ref);
                                    deleteCount += 1;
                                }
                            }
                            return deleteCount;
                        },
                        { maxAttempts: 3 }
                    );
                    deletedCount += deletedInTransaction;
                    setBulkProgress(`Устгаж байна... ${deletedCount.toLocaleString()} / ${eligibleCount.toLocaleString()}`);
                }
                if (pageSnapshot.size < PAGE_SIZE) {
                    deleteFinished = true;
                    break;
                }
            }

            setRefreshKey((previous) => previous + 1);
            setPageCursors([]);
            setCurrentPage(0);
            if (!deleteFinished) {
                throw new Error("Сонгосон хугацааг бүхэлд нь шалгаж дуусаагүй. Үлдсэн бүртгэлийг дахин шалгана уу.");
            }
            await MySwal.fire({
                icon: "success",
                title: "Устгал дууслаа",
                text: `Амжилттай устгасан: ${deletedCount.toLocaleString()} бүртгэл. ${forcedCount > 0 ? "Хуучин хамгаалагдсан бүртгэлийг зөвшөөрлөөр хамруулсан. " : ""}Сүүлийн 24 цагийн бүртгэлүүдийг устгаагүй.`,
                confirmButtonColor: "#2563eb"
            });
        } catch (error) {
            console.error("Бүртгэл бөөнөөр устгах үед алдаа:", error);
            if (deletedCount > 0) {
                setRefreshKey((previous) => previous + 1);
                setPageCursors([]);
                setCurrentPage(0);
            }
            const detail = error?.code === "permission-denied"
                ? "хэрэглэгчид records унших/устгах эрхийг зөв тохируулаагүй байна."
                : getErrorMessage(error, "Сервер ажиллагаа амжилтгүй боллоо.");
            await MySwal.fire({
                icon: "error",
                title: deletedCount > 0 ? "Устгал дутуу дууслаа" : "Устгах боломжгүй",
                text: `${detail} Амжилттай устсан: ${deletedCount.toLocaleString()}. Давтан ажиллуулахаас өмнө үлдсэн түүхийг шалгана уу.`,
                confirmButtonColor: "#ef4444"
            });
        } finally {
            setBulkWorking(false);
            setBulkProgress("");
        }
    };

    /**
     * Урьсан хүний сүүлийн 7 хоногийн урилгын түүхийг нээнэ.
     * Backend нь invitationOwnerId -> phone -> plate гэсэн түлхүүрүүдээр
     * record-уудыг хайж, давхардлыг doc id-аар нэгтгэнэ.
     */
    const openInviterHistory = useCallback(
        async ({
            inviterId = "",
            plate = "",
            building = "",
            number = "",
            phone = ""
        }) => {
            if (!userReady || !parkId) {
                return;
            }

            const normalizedInviterId = String(
                inviterId || ""
            ).trim();

            const normalizedPlate = normalizePlateKey(
                plate
            );

            const normalizedPhone = String(
                phone || ""
            ).trim();

            if (
                !normalizedInviterId &&
                !normalizedPlate &&
                (!normalizedPhone || normalizedPhone === "-")
            ) {
                await MySwal.fire({
                    icon: "info",
                    title: "Урьсан хүнийг тодорхойлох боломжгүй",
                    text: "Энэ хуучин record дээр урьсан хүний ID, утас эсвэл улсын дугаар хадгалагдаагүй байна.",
                    confirmButtonColor: "#3b82f6"
                });
                return;
            }

            const endTime = new Date();
            const startTime = new Date(endTime);
            startTime.setDate(startTime.getDate() - 6);
            startTime.setHours(0, 0, 0, 0);

            const inviter = {
                inviterId: normalizedInviterId,
                plate: normalizedPlate,
                building: String(building || "").trim(),
                number: String(number || "").trim(),
                phone: normalizedPhone
            };

            setInviterHistory({
                open: true,
                loading: true,
                inviter,
                logs: [],
                startTimeISO: startTime.toISOString(),
                endTimeISO: endTime.toISOString(),
                error: "",
                truncated: false
            });

            try {
                const getInviterHistory = httpsCallable(
                    functions,
                    "getInviterHistory"
                );

                const response = await getInviterHistory({
                    parkId,
                    startTimeISO: startTime.toISOString(),
                    endTimeISO: endTime.toISOString(),
                    inviterId: normalizedInviterId,
                    inviterPhone:
                        normalizedPhone === "-"
                            ? ""
                            : normalizedPhone,
                    inviterPlate: normalizedPlate
                });

                const logs = Array.isArray(
                    response.data?.logs
                )
                    ? response.data.logs
                    : [];

                setInviterHistory((previousValue) => ({
                    ...previousValue,
                    loading: false,
                    logs,
                    truncated: Boolean(
                        response.data?.truncated
                    ),
                    error: ""
                }));
            } catch (error) {
                console.error(
                    "Урьсан хүний 7 хоногийн түүх татахад алдаа:",
                    error
                );

                setInviterHistory((previousValue) => ({
                    ...previousValue,
                    loading: false,
                    logs: [],
                    error: getErrorMessage(
                        error,
                        "Урилгын түүхийг татаж чадсангүй."
                    )
                }));
            }
        },
        [functions, parkId, userReady]
    );

    const closeInviterHistory = useCallback(() => {
        setInviterHistory((previousValue) => ({
            ...previousValue,
            open: false
        }));
    }, []);

    /**
     * Хаалганы командыг Firestore-д бичээд local server-ийн
     * SUCCESS / FAILED хариуг хүлээнэ.
     *
     * Firestore write амжилттай болсон нь physical command
     * амжилттай болсон гэсэн үг биш. Dynamic command дээр requestId,
     * legacy command дээр remoteCommandStatus.updatedAt ашиглан яг
     * энэ удаагийн request-ийн server result-ийг баталгаажуулна.
     */
    const openGate = useCallback(
        async (camera) => {
            if (!parkId) {
                await MySwal.fire({
                    icon: "warning",
                    title:
                        "Зогсоолын тохиргоо олдсонгүй",
                    text:
                        "Firebase Auth displayName буюу parkId-ийг шалгана уу.",
                    confirmButtonColor: "#f59e0b"
                });

                return;
            }

            const cameraId = String(
                camera?.camera_id || ""
            )
                .trim()
                .toLowerCase();

            const role =
                String(camera?.role || "")
                    .trim()
                    .toLowerCase() === "exit"
                    ? "exit"
                    : "enter";

            if (!cameraId) {
                await MySwal.fire({
                    icon: "warning",
                    title:
                        "Камерын ID олдсонгүй",
                    confirmButtonColor: "#f59e0b"
                });
                return;
            }

            const parkingReference = doc(
                db,
                "parkings",
                parkId
            );

            try {
                let result;

                /*
                 * Backward compatibility:
                 * Dynamic registry байхгүй хуучин parking:
                 *   gate.enterOpen / gate.exitOpen
                 *
                 * Шинэ parking:
                 *   gate.commands.<cameraId>
                 */
                if (
                    gateCameras.length === 0 &&
                    camera?.legacyField
                ) {
                    const commandId =
                        String(
                            camera.legacyField
                        )
                            .split(".")
                            .pop() ||
                        (role === "exit"
                            ? "exitOpen"
                            : "enterOpen");

                    const beforeSnapshot =
                        await getDoc(
                            parkingReference
                        );

                    const beforeData =
                        beforeSnapshot.exists()
                            ? beforeSnapshot.data() ||
                              {}
                            : {};

                    const previousUpdatedAtKey =
                        timestampKey(
                            beforeData
                                .remoteCommandStatus?.[
                                commandId
                            ]?.updatedAt
                        );

                    await updateDoc(
                        parkingReference,
                        {
                            [camera.legacyField]:
                                true
                        }
                    );

                    result =
                        await waitForLegacyGateCommand(
                            parkingReference,
                            commandId,
                            previousUpdatedAtKey
                        );
                } else {
                    const requestId =
                        createGateCommandId();

                    await updateDoc(
                        parkingReference,
                        {
                            [`gate.commands.${cameraId}`]:
                                {
                                    requestId,
                                    cameraId,
                                    role,
                                    action: "OPEN",
                                    status: "PENDING",
                                    requestedAt:
                                        serverTimestamp(),
                                    requestedBy:
                                        auth.currentUser
                                            ?.email ||
                                        auth.currentUser
                                            ?.uid ||
                                        "dashboard"
                                }
                        }
                    );

                    result =
                        await waitForDynamicGateCommand(
                            parkingReference,
                            cameraId,
                            requestId
                        );
                }

                await MySwal.fire({
                    icon: "success",
                    title:
                        camera?.dashboardLabel ||
                        camera?.name ||
                        cameraId,
                    text:
                        result?.message ||
                        "Сервер хаалт нээх командыг амжилттай гүйцэтгэлээ.",
                    timer: 1800,
                    showConfirmButton: false
                });
            } catch (error) {
                console.error(
                    "Хаалга нээхэд алдаа:",
                    error
                );

                if (
                    error?.code ===
                    "gate/server-timeout"
                ) {
                    await MySwal.fire({
                        icon: "warning",
                        title:
                            "Серверээс хариу ирсэнгүй",
                        text:
                            "Серверт хаалт нээх команд илгээсэн боловч камер хариу өгсөнгүй. Интернет болон камерыг шалгана уу.",
                        confirmButtonColor:
                            "#f59e0b"
                    });
                    return;
                }

                await MySwal.fire({
                    icon: "error",
                    title:
                        "Хаалт нээж чадсангүй",
                    text: getErrorMessage(
                        error,
                        "Local server хаалт нээх командыг амжилттай гүйцэтгэсэнгүй."
                    ),
                    confirmButtonColor:
                        "#ef4444"
                });
            }
        },
        [
            auth,
            db,
            parkId,
            gateCameras
        ]
    );

    const displayedGateCameras =
        gateCameras.length > 0
            ? [...gateCameras]
                  .filter(
                      (camera) =>
                          camera.dashboardVisible !== false
                  )
                  .sort((a, b) => {
                      const orderDiff =
                          Number(a.dashboardOrder || 0) -
                          Number(b.dashboardOrder || 0);

                      if (orderDiff !== 0) {
                          return orderDiff;
                      }

                      if (a.role !== b.role) {
                          return a.role === "enter"
                              ? -1
                              : 1;
                      }

                      return String(a.camera_id).localeCompare(
                          String(b.camera_id)
                      );
                  })
            : LEGACY_GATE_CAMERAS;

    const gateGridClass =
        displayedGateCameras.length <= 1
            ? "lg:tw-grid-cols-1"
            : displayedGateCameras.length === 2
              ? "lg:tw-grid-cols-2"
              : displayedGateCameras.length === 3
                ? "lg:tw-grid-cols-3"
                : "lg:tw-grid-cols-4";

    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
        }
    };

    /**
     * Тээврийн хэрэгслийг блоклох.
     */
    const addBlock = useCallback(
        async (plate, price) => {
            if (!parkId) {
                await MySwal.fire({
                    icon: "warning",
                    title: "Зогсоолын тохиргоо олдсонгүй",
                    text: "Firebase Auth displayName буюу parkId-ийг шалгана уу.",
                    confirmButtonColor: "#f59e0b"
                });

                return;
            }

            const normalizedPlate = String(
                plate || ""
            )
                .trim()
                .toUpperCase();

            if (!normalizedPlate) {
                await MySwal.fire({
                    icon: "warning",
                    title: "Улсын дугаар байхгүй",
                    confirmButtonColor: "#f59e0b"
                });

                return;
            }

            try {
                const registeredCarsReference =
                    collection(
                        db,
                        "parkings",
                        parkId,
                        "registeredCars"
                    );

                const plateQuery = query(
                    registeredCarsReference,
                    where(
                        "plate",
                        "==",
                        normalizedPlate
                    )
                );

                const querySnapshot =
                    await getDocs(
                        plateQuery
                    );

                if (!querySnapshot.empty) {
                    await MySwal.fire({
                        icon: "info",
                        title: "Бүртгэлтэй байна",
                        text: "Энэ машиныг бүртгэлтэй хэсгээс блоклоно уу.",
                        confirmButtonColor: "#3b82f6"
                    });

                    return;
                }

                const result =
                    await MySwal.fire({
                        icon: "warning",
                        title: "Блоклох уу?",
                        text: `${normalizedPlate} дугаартай тээврийн хэрэгслийг блоклох уу?`,
                        showCancelButton: true,
                        confirmButtonColor: "#ef4444",
                        cancelButtonColor: "#94a3b8",
                        confirmButtonText:
                            "Тийм, блоклох",
                        cancelButtonText:
                            "Цуцлах"
                    });

                if (!result.isConfirmed) {
                    return;
                }

                await addDoc(
                    registeredCarsReference,
                    {
                        building: "Гандны",
                        number: "Бүртгэлгүй",
                        isBlocked: true,
                        plate: normalizedPlate,
                        phone: "-",
                        desc:
                            `Төлөөгүй төлбөр ${getCleanPrice(
                                price
                            ).toLocaleString()}₮`,
                        createdAt: new Date()
                    }
                );

                await MySwal.fire({
                    icon: "success",
                    title:
                        "Амжилттай блоклолоо",
                    timer: 1500,
                    showConfirmButton: false
                });
            } catch (error) {
                console.error(
                    "Блоклоход алдаа:",
                    error
                );

                await MySwal.fire({
                    icon: "error",
                    title: "Алдаа гарлаа",
                    text: getErrorMessage(
                        error,
                        "Хадгалж чадсангүй."
                    ),
                    confirmButtonColor: "#ef4444"
                });
            }
        },
        [db, parkId]
    );

    return (
        <div className="tw-bg-[#f4f7f9] tw-min-h-screen tw-font-sans tw-text-slate-900 tw-max-w-[100vw] tw-overflow-x-hidden tw-pb-12">
            {/* HEADER */}
            <header className="tw-bg-white tw-sticky tw-top-0 tw-z-40 tw-border-b tw-border-slate-200 tw-shadow-sm">
                <div className="tw-max-w-[1600px] tw-mx-auto tw-px-3 sm:tw-px-5 lg:tw-px-8 tw-py-3 sm:tw-py-4">
                    <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-start md:tw-items-center tw-gap-4">
                        <div className="tw-flex tw-items-center tw-gap-4 tw-min-w-0">
                            <div className="tw-w-10 tw-h-10 tw-bg-blue-600 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-shadow-md tw-shrink-0">
                                <FiLayers className="tw-w-5 tw-h-5 tw-text-white" />
                            </div>

                            <div className="tw-min-w-0">
                                <h2 className="tw-text-xl tw-font-semibold tw-text-slate-900 tw-m-0 tw-truncate">
                                    {parkingName ||
                                        (
                                            userReady
                                                ? parkId
                                                : "Уншиж байна..."
                                        )}
                                </h2>

                                <p className="tw-text-sm tw-font-normal tw-text-slate-500 tw-mt-0.5 tw-mb-0 tw-truncate">
                                    Хяналтын самбар
                                </p>
                            </div>
                        </div>

                        <div className="tw-flex tw-w-full md:tw-w-auto tw-items-center tw-justify-between md:tw-justify-end tw-gap-3">
                            <div className="tw-flex tw-items-center tw-bg-slate-50 hover:tw-bg-slate-100 tw-border tw-border-slate-200 tw-rounded-xl tw-px-4 tw-py-2 tw-w-full md:tw-w-auto tw-transition-colors">
                                <FiCalendar
                                    className="tw-text-slate-500 tw-mr-2"
                                    size={18}
                                />

                                <DatePicker
                                    selected={selectedDate}
                                    onChange={handleDateChange}
                                    dateFormat="yyyy-MM-dd"
                                    maxDate={new Date()}
                                    locale="mn"
                                    popperPlacement="bottom-end"
                                    className="tw-bg-transparent tw-text-sm tw-font-normal tw-text-slate-900 tw-outline-none tw-w-full sm:tw-w-28 tw-cursor-pointer"
                                />
                            </div>

                            <button
                                type="button"
                                onClick={handleLogout}
                                className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-10 tw-h-10 sm:tw-w-auto sm:tw-px-4 sm:tw-py-2.5 tw-bg-white tw-border tw-border-slate-200 hover:tw-border-rose-200 hover:tw-bg-rose-50 hover:tw-text-rose-600 tw-text-slate-900 tw-font-normal tw-text-sm tw-rounded-xl tw-transition-colors tw-flex-shrink-0"
                            >
                                <FiLogOut size={16} />

                                <span className="tw-hidden sm:tw-inline">
                                    Гарах
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="tw-max-w-[1600px] tw-mx-auto tw-px-3 sm:tw-px-5 lg:tw-px-8 tw-py-4 sm:tw-py-6 lg:tw-py-8 tw-space-y-5 sm:tw-space-y-7 lg:tw-space-y-8">
                {/* ХААЛГА УДИРДАХ */}
                <div className="tw-bg-slate-900 tw-rounded-2xl sm:tw-rounded-[2rem] tw-p-4 sm:tw-p-6 lg:tw-p-8 tw-shadow-2xl tw-shadow-slate-900/20 tw-relative tw-overflow-hidden">
                    <div className="tw-absolute tw-top-0 tw-right-0 tw-w-96 tw-h-96 tw-bg-blue-500/10 tw-blur-3xl tw-rounded-full tw-translate-x-1/3 -tw-translate-y-1/3 tw-pointer-events-none" />

                    <div className="tw-absolute tw-bottom-0 tw-left-0 tw-w-64 tw-h-64 tw-bg-emerald-500/10 tw-blur-3xl tw-rounded-full -tw-translate-x-1/3 tw-translate-y-1/3 tw-pointer-events-none" />

                    <div className="tw-relative tw-z-10">
                        <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
                            <div>
                                <h3 className="tw-text-lg sm:tw-text-xl tw-font-normal tw-text-white tw-tracking-wide">
                                    Зогсоолын хаалга удирдах
                                </h3>

                                <p className="tw-text-slate-400 tw-text-xs sm:tw-text-sm tw-mt-1">
                                    Бодит хугацаанд хаалга нээх удирдлага
                                </p>
                            </div>

                            <div className="tw-hidden sm:tw-flex tw-items-center tw-gap-2 tw-bg-slate-800 tw-px-3 tw-py-1.5 tw-rounded-full tw-border tw-border-slate-700">
                                <span className="tw-relative tw-flex tw-h-2.5 tw-w-2.5">
                                    <span className="tw-animate-ping tw-absolute tw-inline-flex tw-h-full tw-w-full tw-rounded-full tw-bg-emerald-400 tw-opacity-75" />

                                    <span className="tw-relative tw-inline-flex tw-rounded-full tw-h-2.5 tw-w-2.5 tw-bg-emerald-500" />
                                </span>

                                <span className="tw-text-xs tw-font-normal tw-text-slate-300">
                                    Систем хэвийн
                                </span>
                            </div>
                        </div>

                        <div
                            className={`
                                tw-grid
                                tw-grid-cols-1
                                sm:tw-grid-cols-2
                                ${gateGridClass}
                                tw-gap-2.5
                                sm:tw-gap-3
                                lg:tw-gap-4
                            `}
                        >
                            {displayedGateCameras.map(
                                (camera) => (
                                    <GateButton
                                        key={camera.camera_id}
                                        onClick={() =>
                                            openGate(camera)
                                        }
                                        icon={
                                            camera.role === "exit"
                                                ? FiLogOut
                                                : FiLogIn
                                        }
                                        label={
                                            `${
                                                camera.dashboardLabel ||
                                                camera.name ||
                                                camera.camera_id
                                            } НЭЭХ`
                                        }
                                        disabled={
                                            !userReady ||
                                            camera.enabled === false ||
                                            camera.configured === false
                                        }
                                        gradientClass={
                                            camera.role === "exit"
                                                ? "tw-bg-gradient-to-b tw-from-blue-500 tw-to-blue-700 tw-border tw-border-blue-400/50"
                                                : "tw-bg-gradient-to-b tw-from-emerald-400 tw-to-emerald-600 tw-border tw-border-emerald-400/50"
                                        }
                                        shadowClass={
                                            camera.role === "exit"
                                                ? "tw-shadow-[0_8px_20px_rgba(59,130,246,0.25)] hover:tw-shadow-[0_8px_25px_rgba(59,130,246,0.4)]"
                                                : "tw-shadow-[0_8px_20px_rgba(16,185,129,0.25)] hover:tw-shadow-[0_8px_25px_rgba(16,185,129,0.4)]"
                                        }
                                    />
                                )
                            )}
                        </div>

                        {gateCameras.length === 0 && (
                            <p className="tw-mt-3 tw-text-xs tw-text-amber-300">
                                Default config
                            </p>
                        )}
                    </div>
                </div>

                {/* KPI ҮЗҮҮЛЭЛТ */}
                <div>
                    <h3 className="tw-text-lg tw-font-normal tw-text-slate-900 tw-mb-4 tw-px-1">
                        Өнөөдрийн үзүүлэлт
                    </h3>

                    <section className={`tw-grid tw-grid-cols-1 min-[480px]:tw-grid-cols-2 ${canViewDailyFinance ? "lg:tw-grid-cols-4" : "lg:tw-grid-cols-2"} tw-gap-3 sm:tw-gap-4 lg:tw-gap-6 tw-mb-6 sm:tw-mb-8`}>
                        <StatCard
                            label="Нийт орсон"
                            value={
                                isAggregating
                                    ? "..."
                                    : totalEnteredCount
                            }
                            icon={FiTrendingUp}
                            colorClass="tw-text-blue-600"
                            bgClass="tw-bg-blue-50"
                        />

                        <StatCard
                            label="Одоо зогсоолд"
                            value={
                                isAggregating
                                    ? "..."
                                    : occupiedSpots
                            }
                            icon={FiHome}
                            colorClass="tw-text-emerald-600"
                            bgClass="tw-bg-emerald-50"
                        />

                        {canViewDailyFinance && (
                            <>
                                <StatCard
                                    label="Орлого"
                                    value={
                                        isAggregating
                                            ? "..."
                                            : `${totalRevenue.toLocaleString()}₮`
                                    }
                                    icon={FiDollarSign}
                                    colorClass="tw-text-indigo-600"
                                    bgClass="tw-bg-indigo-50"
                                />

                                <StatCard
                                    label="Алдагдал"
                                    value={
                                        isAggregating
                                            ? "..."
                                            : `${totalLoss.toLocaleString()}₮`
                                    }
                                    icon={FiActivity}
                                    colorClass="tw-text-rose-600"
                                    bgClass="tw-bg-rose-50"
                                />
                            </>
                        )}
                    </section>
                </div>

                {/* БҮРТГЭЛИЙН ХҮСНЭГТ */}
                <div className="tw-bg-white tw-rounded-[1.5rem] tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden tw-flex tw-flex-col">
                    {/* ХАЙЛТ, ШҮҮЛТҮҮР */}
                    <div className="tw-p-4 sm:tw-p-6 tw-border-b tw-border-slate-100 tw-flex tw-flex-col lg:tw-flex-row tw-justify-between tw-items-stretch lg:tw-items-center tw-gap-4">
                        <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 tw-w-full lg:tw-w-auto">
                            <div className="tw-relative tw-w-full lg:tw-w-80">
                                <FiSearch
                                    className="tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-500"
                                    size={18}
                                />

                                <input
                                    type="text"
                                    placeholder="Улсын дугаараар хайх..."
                                    value={searchInput}
                                    onChange={(event) =>
                                        setSearchInput(
                                            event.target.value
                                        )
                                    }
                                    className="tw-w-full tw-pl-11 tw-pr-4 tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-sm tw-font-normal tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-bg-white focus:tw-border-blue-400 focus:tw-ring-2 focus:tw-ring-blue-100 tw-transition-all"
                                />
                            </div>

                            <button
                                type="button"
                                onClick={handleRefresh}
                                disabled={
                                    isLoadingData ||
                                    isAggregating ||
                                    isRefreshing ||
                                    !userReady
                                }
                                title="KPI болон хүснэгтийн мэдээллийг шинэчлэх"
                                className="tw-flex tw-h-11 tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-text-sm tw-font-medium tw-text-slate-700 tw-transition-colors hover:tw-bg-slate-50 hover:tw-text-blue-600 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                            >
                                <FiRefreshCw
                                    size={17}
                                    className={
                                        isRefreshing
                                            ? "tw-animate-spin"
                                            : ""
                                    }
                                />

                                <span>Шинэчлэх</span>
                            </button>

                            {canBulkDelete && (
                                <button
                                    type="button"
                                    disabled={bulkWorking}
                                    onClick={() => setShowBulkDelete((current) => !current)}
                                    title="Огноо сонгож зогсоолын бүртгэл бөөнөөр устгах хэсгийг нээх"
                                    className="tw-flex tw-h-11 tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-border-rose-300 tw-bg-rose-600 tw-px-4 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm hover:tw-bg-rose-700 disabled:tw-opacity-50"
                                >
                                    <FiTrash2 size={16} />
                                    <span>{showBulkDelete ? "Устгах хэсгийг хаах" : "Хугацаагаар бөөнөөр устгах"}</span>
                                </button>
                            )}
                        </div>

                        <div className="tw-flex tw-bg-slate-50 tw-border tw-border-slate-200 tw-p-1 tw-rounded-xl tw-w-full lg:tw-w-auto tw-overflow-x-auto">
                            <button
                                type="button"
                                onClick={() =>
                                    setPaymentFilter(
                                        "all"
                                    )
                                }
                                className={`
                                    tw-flex-1
                                    lg:tw-flex-none
                                    tw-px-5
                                    tw-py-2
                                    tw-rounded-lg
                                    tw-text-xs
                                    sm:tw-text-sm
                                    tw-font-normal
                                    tw-transition-all
                                    tw-duration-200
                                    tw-whitespace-nowrap
                                    ${
                                        paymentFilter ===
                                        "all"
                                            ? "tw-bg-white tw-text-slate-900 tw-shadow-sm tw-border tw-border-slate-200"
                                            : "tw-text-slate-600 hover:tw-text-slate-900"
                                    }
                                `}
                            >
                                Бүгд
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    setPaymentFilter(
                                        "paid"
                                    )
                                }
                                className={`
                                    tw-flex-1
                                    lg:tw-flex-none
                                    tw-px-5
                                    tw-py-2
                                    tw-rounded-lg
                                    tw-text-xs
                                    sm:tw-text-sm
                                    tw-font-normal
                                    tw-transition-all
                                    tw-duration-200
                                    tw-whitespace-nowrap
                                    ${
                                        paymentFilter ===
                                        "paid"
                                            ? "tw-bg-emerald-500 tw-text-white tw-shadow-sm"
                                            : "tw-text-slate-600 hover:tw-text-emerald-600"
                                    }
                                `}
                            >
                                Төлөгдсөн
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    setPaymentFilter(
                                        "unpaid"
                                    )
                                }
                                className={`
                                    tw-flex-1
                                    lg:tw-flex-none
                                    tw-px-5
                                    tw-py-2
                                    tw-rounded-lg
                                    tw-text-xs
                                    sm:tw-text-sm
                                    tw-font-normal
                                    tw-transition-all
                                    tw-duration-200
                                    tw-whitespace-nowrap
                                    ${
                                        paymentFilter ===
                                        "unpaid"
                                            ? "tw-bg-rose-500 tw-text-white tw-shadow-sm"
                                            : "tw-text-slate-600 hover:tw-text-rose-600"
                                    }
                                `}
                            >
                                Төлөөгүй
                            </button>
                        </div>
                    </div>

                    {/* ХУГАЦААГААР БӨӨНӨӨР УСТГАХ — зөвхөн superadmin-д харагдана */}
                    {canBulkDelete && showBulkDelete && (
                        <div className="tw-p-4 sm:tw-p-6 tw-border-b tw-border-rose-100 tw-bg-rose-50/60">
                            <div className="tw-flex tw-flex-col tw-gap-1 tw-mb-4">
                                <h4 className="tw-text-base tw-font-semibold tw-text-rose-900 tw-m-0">
                                    <FiTrash2 className="tw-inline-block tw-mr-2" />Хугацаагаар бүртгэл устгах
                                </h4>
                                <p className="tw-text-xs sm:tw-text-sm tw-text-slate-600 tw-m-0">
                                    Сонгосон хугацааны гаралт бүртгэгдсэн түүхийг Firebase-ээс шууд устгана. Гарсан хугацаагүй эсвэл идэвхтэй төлөвтэй хуучин бүртгэл анхнаасаа хамгаалагдана; шаардлагатай бол доорх тусгай сонголтоор хамруулж болно. Сүүлийн 24 цагийг аль ч тохиргоонд устгахгүй. Cloud Function нэмэхгүй.
                                </p>

                            </div>
                            <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-end tw-gap-3">
                                <label className="tw-flex tw-flex-col tw-gap-1 tw-text-xs tw-font-semibold tw-text-slate-700">
                                    Эхлэх огноо
                                    <DatePicker
                                        selected={bulkStartDate}
                                        onChange={setBulkStartDate}
                                        selectsStart
                                        startDate={bulkStartDate}
                                        endDate={bulkEndDate}
                                        maxDate={bulkEndDate || new Date(new Date().setDate(new Date().getDate() - 1))}
                                        isClearable
                                        dateFormat="yyyy-MM-dd"
                                        locale="mn"
                                        placeholderText="Эхлэх өдөр"
                                        className="tw-w-full sm:tw-w-44 tw-px-3 tw-py-2.5 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-text-sm tw-text-slate-900"
                                    />
                                </label>
                                <label className="tw-flex tw-flex-col tw-gap-1 tw-text-xs tw-font-semibold tw-text-slate-700">
                                    Дуусах огноо
                                    <DatePicker
                                        selected={bulkEndDate}
                                        onChange={setBulkEndDate}
                                        selectsEnd
                                        startDate={bulkStartDate}
                                        endDate={bulkEndDate}
                                        minDate={bulkStartDate || undefined}
                                        maxDate={new Date(new Date().setDate(new Date().getDate() - 1))}
                                        isClearable
                                        dateFormat="yyyy-MM-dd"
                                        locale="mn"
                                        placeholderText="Дуусах өдөр"
                                        className="tw-w-full sm:tw-w-44 tw-px-3 tw-py-2.5 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-text-sm tw-text-slate-900"
                                    />
                                </label>
                                <button
                                    type="button"
                                    onClick={handleBulkDeleteRecords}
                                    disabled={bulkWorking || !bulkStartDate || !bulkEndDate}
                                    className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-bg-rose-600 tw-text-white tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium hover:tw-bg-rose-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                >
                                    {bulkWorking ? <FiLoader className="tw-animate-spin" /> : <FiTrash2 />}
                                    Шалгаж устгах
                                </button>
                            </div>
                            <label className="tw-flex tw-items-start tw-gap-3 tw-mt-4 tw-text-sm tw-text-rose-900 tw-cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={bulkIncludeProtected}
                                    disabled={bulkWorking}
                                    onChange={(event) => setBulkIncludeProtected(event.target.checked)}
                                    className="tw-mt-1 tw-w-4 tw-h-4 tw-accent-rose-700"
                                />
                                <span>
                                    <strong>Хамгаалагдсан хуучин бүртгэлийг мөн устгах</strong>
                                    <span className="tw-block tw-text-xs tw-text-rose-700 tw-mt-1">
                                        24 цагаас өмнөх, гарсан хугацаа байхгүй эсвэл идэвхтэй гэж тэмдэглэгдсэн бүртгэлд хамаарна.
                                        Бодит зогсолт, төлбөрт нөлөөлж болох тул зөвхөн нягталсны дараа сонгоно уу.
                                    </span>
                                </span>
                            </label>
                            {bulkProgress && (
                                <p className="tw-text-sm tw-text-rose-800 tw-mt-3 tw-mb-0" role="status">
                                    {bulkProgress}
                                </p>
                            )}
                            <p className="tw-text-xs tw-text-rose-700 tw-mt-3 tw-mb-0">
                            </p>
                        </div>
                    )}

                    {/* TABLE */}
                    <div className="tw-overflow-x-auto tw-overflow-y-auto tw-w-full tw-max-h-[600px] tw-relative">
                        <table className="tw-w-full tw-text-left tw-border-collapse tw-min-w-[1080px]">
                            <thead className="tw-bg-slate-50 tw-sticky tw-top-0 tw-z-20 tw-shadow-sm">
                                <tr>
                                    <th className="tw-px-6 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-w-16 tw-border-b tw-border-slate-200">
                                        №
                                    </th>

                                    <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-border-b tw-border-slate-200">
                                        Улсын дугаар
                                    </th>

                                    <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-border-b tw-border-slate-200">
                                        Төрөл
                                    </th>

                                    <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-border-b tw-border-slate-200">
                                        Орсон цаг
                                    </th>

                                    <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-border-b tw-border-slate-200">
                                        Гарсан цаг
                                    </th>

                                    <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-border-b tw-border-slate-200">
                                        Хугацаа
                                    </th>

                                    <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-border-b tw-border-slate-200">
                                        Төлбөр
                                    </th>

                                    <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-border-b tw-border-slate-200">
                                        Төлөв
                                    </th>

                                    <th className="tw-px-6 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-text-right tw-border-b tw-border-slate-200 tw-min-w-[250px]">
                                        Удирдах
                                    </th>
                                </tr>
                            </thead>

                            <tbody className="tw-divide-y tw-divide-slate-100 tw-bg-white">
                                {isLoadingData ? (
                                    <tr>
                                        <td
                                            colSpan={9}
                                            className="tw-px-6 tw-py-24 tw-text-center"
                                        >
                                            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-blue-500">
                                                <div className="tw-w-10 tw-h-10 tw-border-4 tw-border-blue-100 tw-border-t-blue-600 tw-rounded-full tw-animate-spin tw-mb-4" />

                                                <span className="tw-text-sm tw-font-normal tw-text-slate-900">
                                                    Уншиж байна...
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : parkingLogs.length ===
                                  0 ? (
                                    <tr>
                                        <td
                                            colSpan={9}
                                            className="tw-px-6 tw-py-24 tw-text-center"
                                        >
                                            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-slate-400">
                                                <div className="tw-w-16 tw-h-16 tw-bg-slate-50 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4 tw-border tw-border-slate-100">
                                                    <FiSearch
                                                        size={
                                                            24
                                                        }
                                                        className="tw-text-slate-400"
                                                    />
                                                </div>

                                                <span className="tw-text-sm tw-font-normal tw-text-slate-900">
                                                    Мэдээлэл олдсонгүй.
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    parkingLogs.map(
                                        (
                                            log,
                                            index
                                        ) => {
                                            const cleanPrice =
                                                getCleanPrice(
                                                    log.price
                                                );

                                            const vehiclePlate =
                                                String(
                                                    log.car_plate ||
                                                        log.plate ||
                                                        "-"
                                                );

                                            const statusText =
                                                log.status ===
                                                "parked"
                                                    ? "Зогсоолд"
                                                    : log.status ||
                                                      "-";

                                            const lowerStatus =
                                                String(
                                                    log.status ||
                                                        ""
                                                ).toLowerCase();

                                            const isActiveStatus =
                                                log.status ===
                                                    "parked" ||
                                                lowerStatus.includes(
                                                    "тооцоолж"
                                                );

                                            const isWarningStatus =
                                                lowerStatus.includes(
                                                    "шаардлагатай"
                                                ) ||
                                                lowerStatus.includes(
                                                    "хэтэрсэн"
                                                );

                                            const paymentText =
                                                log.paymentStatus ||
                                                (
                                                    log.payment
                                                        ? "Төлсөн"
                                                        : cleanPrice >
                                                          0
                                                          ? "Төлөөгүй"
                                                          : "Төлбөргүй"
                                                );

                                            const isInvited =
                                                log.invited ===
                                                    true ||
                                                String(
                                                    log.accessType ||
                                                        ""
                                                )
                                                    .trim()
                                                    .toLowerCase() ===
                                                    "invited";

                                            const inviterPlate =
                                                normalizePlateKey(
                                                    log.invitationOwnerPlate
                                                );

                                            const inviterBuilding =
                                                String(
                                                    log.invitationOwnerBuilding ||
                                                        ""
                                                ).trim();

                                            const inviterNumber =
                                                String(
                                                    log.invitationOwnerNumber ||
                                                        ""
                                                ).trim();

                                            const inviterPhone =
                                                String(
                                                    log.invitationOwnerPhone ||
                                                        ""
                                                ).trim();

                                            const inviterLocation =
                                                [
                                                    inviterBuilding,
                                                    inviterNumber
                                                        ? `${inviterNumber} тоот`
                                                        : ""
                                                ]
                                                    .filter(Boolean)
                                                    .join(" · ");

                                            return (
                                                <tr
                                                    key={
                                                        log.id ||
                                                        `${vehiclePlate}-${index}`
                                                    }
                                                    className="hover:tw-bg-slate-50 tw-transition-colors tw-duration-150"
                                                >
                                                    <td className="tw-px-6 tw-py-4 tw-text-sm tw-font-normal tw-text-slate-900">
                                                        {currentPage *
                                                            pageSize +
                                                            index +
                                                            1}
                                                    </td>

                                                    <td className="tw-px-4 tw-py-4">
                                                        <div className="tw-inline-flex tw-items-center tw-px-3 tw-py-1.5 tw-bg-white tw-border tw-border-slate-300 tw-rounded-md tw-text-[13px] tw-font-bold tw-text-slate-900 tw-tracking-widest tw-uppercase">
                                                            {
                                                                vehiclePlate
                                                            }
                                                        </div>
                                                    </td>

                                                    <td className="tw-px-4 tw-py-4 tw-text-sm tw-font-normal tw-text-slate-900">
                                                        {log.isLarge
                                                            ? "Том оврын"
                                                            : "Суудлын"}
                                                    </td>

                                                    <td className="tw-px-4 tw-py-4">
                                                        <span className="tw-text-sm tw-font-normal tw-text-slate-900">
                                                            {formatDateTime(
                                                                log.entry_time
                                                            )}
                                                        </span>
                                                    </td>

                                                    <td className="tw-px-4 tw-py-4">
                                                        <span className="tw-text-sm tw-font-normal tw-text-slate-900">
                                                            {formatDateTime(
                                                                log.exit_time
                                                            )}
                                                        </span>
                                                    </td>

                                                    <td className="tw-px-4 tw-py-4 tw-text-sm tw-font-normal tw-text-slate-900">
                                                        {log.duration ||
                                                            "-"}
                                                    </td>

                                                    <td className="tw-px-4 tw-py-4">
                                                        <span className="tw-text-sm tw-font-normal tw-text-slate-900">
                                                            {`${cleanPrice.toLocaleString()}₮`}
                                                        </span>
                                                    </td>

                                                    <td className="tw-px-4 tw-py-4">
                                                        <span
                                                            className={`
                                                                tw-inline-flex
                                                                tw-items-center
                                                                tw-gap-1.5
                                                                tw-px-3
                                                                tw-py-1.5
                                                                tw-rounded-lg
                                                                tw-text-[11px]
                                                                tw-font-normal
                                                                tw-uppercase
                                                                tw-tracking-widest
                                                                tw-shadow-sm
                                                                ${
                                                                    log.payment
                                                                        ? "tw-bg-emerald-500 tw-text-white"
                                                                        : cleanPrice >
                                                                          0
                                                                          ? "tw-bg-rose-500 tw-text-white"
                                                                          : "tw-bg-slate-200 tw-text-slate-900"
                                                                }
                                                            `}
                                                        >
                                                            {
                                                                paymentText
                                                            }
                                                        </span>
                                                    </td>

                                                    <td className="tw-px-6 tw-py-4 tw-text-right">
                                                        <div className="tw-flex tw-items-start tw-justify-end tw-gap-3">
                                                            <div className="tw-flex tw-min-w-[170px] tw-flex-col tw-items-end tw-gap-1.5">
                                                                <span
                                                                    className={`
                                                                        tw-inline-flex
                                                                        tw-items-center
                                                                        tw-gap-1.5
                                                                        tw-px-3
                                                                        tw-py-1.5
                                                                        tw-rounded-lg
                                                                        tw-text-[11px]
                                                                        tw-font-normal
                                                                        tw-uppercase
                                                                        tw-tracking-widest
                                                                        tw-shadow-sm
                                                                        ${
                                                                            isActiveStatus
                                                                                ? "tw-bg-blue-500 tw-text-white"
                                                                                : isWarningStatus
                                                                                  ? "tw-bg-rose-500 tw-text-white"
                                                                                  : "tw-bg-slate-200 tw-text-slate-900"
                                                                        }
                                                                    `}
                                                                >
                                                                    {isActiveStatus && (
                                                                        <span className="tw-relative tw-flex tw-h-2 tw-w-2">
                                                                            <span className="tw-animate-ping tw-absolute tw-inline-flex tw-h-full tw-w-full tw-rounded-full tw-bg-white tw-opacity-75" />

                                                                            <span className="tw-relative tw-inline-flex tw-rounded-full tw-h-2 tw-w-2 tw-bg-white" />
                                                                        </span>
                                                                    )}

                                                                    {statusText}
                                                                </span>

                                                                {isInvited && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            openInviterHistory({
                                                                                inviterId:
                                                                                    log.invitationOwnerId,
                                                                                plate:
                                                                                    inviterPlate,
                                                                                building:
                                                                                    inviterBuilding,
                                                                                number:
                                                                                    inviterNumber,
                                                                                phone:
                                                                                    inviterPhone
                                                                            })
                                                                        }
                                                                        className="tw-group tw-max-w-[230px] tw-rounded-lg tw-border tw-border-blue-100 tw-bg-blue-50/70 tw-px-2.5 tw-py-2 tw-text-right hover:tw-bg-blue-100 hover:tw-border-blue-200 tw-transition-colors tw-cursor-pointer focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-300"
                                                                        title="Сүүлийн 7 хоногийн урилгын түүх харах"
                                                                    >
                                                                        <div className="tw-text-[10px] tw-font-medium tw-text-slate-500">
                                                                            Урьсан · 7 хоногийн түүх
                                                                        </div>

                                                                        <div className="tw-mt-0.5 tw-text-[12px] tw-font-bold tw-text-blue-700 tw-tracking-wider tw-uppercase group-hover:tw-text-blue-800">
                                                                            {inviterPlate || "Тодорхойгүй"}
                                                                        </div>

                                                                        {inviterLocation && (
                                                                            <div className="tw-mt-0.5 tw-text-[10px] tw-font-normal tw-text-slate-500">
                                                                                {inviterLocation}
                                                                            </div>
                                                                        )}

                                                                        {inviterPhone &&
                                                                            inviterPhone !== "-" && (
                                                                                <div className="tw-mt-0.5 tw-text-[10px] tw-text-slate-400">
                                                                                    {inviterPhone}
                                                                                </div>
                                                                            )}
                                                                    </button>
                                                                )}
                                                            </div>

                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    addBlock(
                                                                        log.car_plate ||
                                                                            log.plate,
                                                                        log.price
                                                                    )
                                                                }
                                                                disabled={
                                                                    Boolean(
                                                                        log.registered
                                                                    ) ||
                                                                    ![
                                                                        "admin",
                                                                        "master"
                                                                    ].includes(
                                                                        userRole
                                                                    )
                                                                }
                                                                title={
                                                                    log.registered
                                                                        ? "Бүртгэлтэй хэрэглэгч"
                                                                        : ![
                                                                            "admin",
                                                                            "master"
                                                                        ].includes(
                                                                            userRole
                                                                        )
                                                                          ? "Блоклох эрх байхгүй"
                                                                          : "Блоклох"
                                                                }
                                                                className={`
                                                                    tw-flex
                                                                    tw-items-center
                                                                    tw-justify-center
                                                                    tw-w-8
                                                                    tw-h-8
                                                                    tw-rounded-xl
                                                                    tw-transition-all
                                                                    tw-duration-200
                                                                    tw-shadow-sm
                                                                    active:tw-scale-95
                                                                    disabled:tw-cursor-not-allowed
                                                                    ${
                                                                        log.registered
                                                                            ? "tw-bg-emerald-500 tw-text-white"
                                                                            : [
                                                                                "admin",
                                                                                "master"
                                                                            ].includes(
                                                                                userRole
                                                                            )
                                                                              ? "tw-bg-white tw-text-slate-900 tw-border tw-border-slate-300 hover:tw-bg-rose-500 hover:tw-text-white hover:tw-border-rose-500"
                                                                              : "tw-bg-slate-100 tw-text-slate-400 tw-border tw-border-slate-200"
                                                                    }
                                                                `}
                                                            >
                                                                {log.registered ? (
                                                                    <FiHome
                                                                        size={
                                                                            15
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <FiLock
                                                                        size={
                                                                            15
                                                                        }
                                                                    />
                                                                )}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINATION */}
                    <div className="tw-px-6 tw-py-4 tw-border-t tw-border-slate-100 tw-bg-white tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-gap-4">
                        <div className="tw-text-sm tw-font-normal tw-text-slate-500">
                            Хуудас{" "}

                            <span className="tw-text-slate-900 tw-font-medium">
                                {currentPage + 1}
                            </span>
                        </div>

                        <div className="tw-flex tw-items-center tw-gap-2 tw-w-full sm:tw-w-auto">
                            <button
                                type="button"
                                onClick={handlePrevious}
                                disabled={
                                    currentPage === 0 ||
                                    isLoadingData
                                }
                                className="tw-flex-1 sm:tw-flex-none tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-normal tw-transition-colors tw-border tw-border-slate-200 tw-bg-white tw-text-slate-900 hover:tw-bg-slate-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                            >
                                Өмнөх
                            </button>

                            <button
                                type="button"
                                onClick={handleNext}
                                disabled={
                                    !hasNextPage ||
                                    isLoadingData
                                }
                                className="tw-flex-1 sm:tw-flex-none tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-normal tw-transition-colors tw-bg-slate-900 tw-text-white hover:tw-bg-slate-800 disabled:tw-bg-slate-200 disabled:tw-text-slate-500 disabled:tw-cursor-not-allowed"
                            >
                                Дараах
                            </button>
                        </div>
                    </div>
                </div>
            </main>

            {/* =========================================================
                УРЬСАН ХҮНИЙ СҮҮЛИЙН 7 ХОНОГИЙН ТҮҮХ
               ========================================================= */}
            {inviterHistory.open && (() => {
                const summary = summarizeInviterHistory(
                    inviterHistory.logs
                );

                const inviter =
                    inviterHistory.inviter || {};

                const inviterLocation = [
                    inviter.building,
                    inviter.number
                        ? `${inviter.number} тоот`
                        : ""
                ]
                    .filter(Boolean)
                    .join(" · ");

                const startDate = toDateObject(
                    inviterHistory.startTimeISO
                );

                const endDate = toDateObject(
                    inviterHistory.endTimeISO
                );

                const rangeText =
                    startDate && endDate
                        ? `${formatDateTime(startDate).split(" ")[0]} – ${formatDateTime(endDate).split(" ")[0]}`
                        : "Сүүлийн 7 хоног";

                return (
                    <div className="tw-fixed tw-inset-0 tw-z-[2000] tw-bg-slate-950/60 tw-backdrop-blur-sm tw-flex tw-items-center tw-justify-center tw-p-3 sm:tw-p-6">
                        <div className="tw-bg-white tw-w-full tw-max-w-6xl tw-max-h-[92vh] tw-rounded-2xl sm:tw-rounded-3xl tw-shadow-2xl tw-overflow-hidden tw-flex tw-flex-col">
                            {/* HEADER */}
                            <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-px-4 sm:tw-px-6 tw-py-4 sm:tw-py-5 tw-border-b tw-border-slate-200 tw-bg-white">
                                <div className="tw-min-w-0">
                                    <div className="tw-flex tw-items-center tw-gap-2 tw-text-blue-600 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider">
                                        <FiUser size={14} />
                                        Урьсан хэрэглэгч
                                    </div>

                                    <h3 className="tw-mt-1 tw-text-xl sm:tw-text-2xl tw-font-bold tw-text-slate-900 tw-tracking-wide tw-uppercase">
                                        {inviter.plate ||
                                            "Тодорхойгүй"}
                                    </h3>

                                    <div className="tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-xs sm:tw-text-sm tw-text-slate-500">
                                        {inviterLocation && (
                                            <span>
                                                {inviterLocation}
                                            </span>
                                        )}

                                        {inviter.phone &&
                                            inviter.phone !== "-" && (
                                                <span>
                                                    {inviter.phone}
                                                </span>
                                            )}

                                        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-slate-400">
                                            <FiCalendar size={13} />
                                            {rangeText}
                                        </span>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={closeInviterHistory}
                                    className="tw-w-9 tw-h-9 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-border tw-border-slate-200 tw-text-slate-500 hover:tw-bg-slate-100 hover:tw-text-slate-900 tw-transition-colors tw-shrink-0"
                                    aria-label="Хаах"
                                >
                                    <FiX size={20} />
                                </button>
                            </div>

                            <div className="tw-flex-1 tw-overflow-y-auto tw-bg-slate-50 tw-p-4 sm:tw-p-6">
                                {inviterHistory.loading ? (
                                    <div className="tw-min-h-[360px] tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-slate-500">
                                        <FiLoader className="tw-w-8 tw-h-8 tw-text-blue-600 tw-animate-spin" />
                                        <div className="tw-mt-3 tw-text-sm tw-font-medium tw-text-slate-900">
                                            7 хоногийн урилгын түүх уншиж байна...
                                        </div>
                                    </div>
                                ) : inviterHistory.error ? (
                                    <div className="tw-min-h-[320px] tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center">
                                        <div className="tw-max-w-lg tw-bg-white tw-border tw-border-rose-200 tw-rounded-2xl tw-p-6 tw-shadow-sm">
                                            <div className="tw-text-rose-600 tw-font-bold">
                                                Урилгын түүх татаж чадсангүй
                                            </div>
                                            <div className="tw-mt-2 tw-text-sm tw-text-slate-500 tw-leading-6">
                                                {inviterHistory.error}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    openInviterHistory(
                                                        inviter
                                                    )
                                                }
                                                className="tw-mt-4 tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-rounded-xl tw-bg-slate-900 tw-text-white tw-text-sm tw-font-medium hover:tw-bg-slate-800"
                                            >
                                                <FiRefreshCw size={15} />
                                                Дахин оролдох
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {/* KPI */}
                                        <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3 sm:tw-gap-4">
                                            <div className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-2xl tw-p-4 sm:tw-p-5 tw-shadow-sm">
                                                <div className="tw-flex tw-items-center tw-gap-2 tw-text-slate-500 tw-text-xs">
                                                    <FiActivity size={15} />
                                                    Нийт урилга
                                                </div>
                                                <div className="tw-mt-2 tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-slate-900">
                                                    {summary.total}
                                                </div>
                                            </div>

                                            <div className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-2xl tw-p-4 sm:tw-p-5 tw-shadow-sm">
                                                <div className="tw-flex tw-items-center tw-gap-2 tw-text-slate-500 tw-text-xs">
                                                    <FiUsers size={15} />
                                                    Өөр машин
                                                </div>
                                                <div className="tw-mt-2 tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-slate-900">
                                                    {summary.uniqueVehicles}
                                                </div>
                                            </div>

                                            <div className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-2xl tw-p-4 sm:tw-p-5 tw-shadow-sm">
                                                <div className="tw-flex tw-items-center tw-gap-2 tw-text-slate-500 tw-text-xs">
                                                    <FiClock size={15} />
                                                    Өнөөдөр
                                                </div>
                                                <div className="tw-mt-2 tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-blue-600">
                                                    {summary.todayCount}
                                                </div>
                                            </div>

                                            <div className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-2xl tw-p-4 sm:tw-p-5 tw-shadow-sm">
                                                <div className="tw-flex tw-items-center tw-gap-2 tw-text-slate-500 tw-text-xs">
                                                    <FiHome size={15} />
                                                    Одоо зогсоолд
                                                </div>
                                                <div className="tw-mt-2 tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-emerald-600">
                                                    {summary.parkedCount}
                                                </div>
                                            </div>
                                        </div>

                                        {inviterHistory.truncated && (
                                            <div className="tw-mt-4 tw-rounded-xl tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-4 tw-py-3 tw-text-xs sm:tw-text-sm tw-text-amber-800">
                                                Урилгын тоо хамгаалалтын дээд хязгаарт хүрсэн тул хамгийн сүүлийн хэсгийг харуулж байна.
                                            </div>
                                        )}

                                        {/* DETAILS */}
                                        <div className="tw-mt-5 tw-bg-white tw-border tw-border-slate-200 tw-rounded-2xl tw-shadow-sm tw-overflow-hidden">
                                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 sm:tw-px-5 tw-py-4 tw-border-b tw-border-slate-200">
                                                <div>
                                                    <h4 className="tw-text-sm sm:tw-text-base tw-font-bold tw-text-slate-900">
                                                        Урьж оруулсан машинууд
                                                    </h4>
                                                    <p className="tw-mt-0.5 tw-text-xs tw-text-slate-500">
                                                        Сүүлийн 7 хоногийн дэлгэрэнгүй хөдөлгөөн
                                                    </p>
                                                </div>
                                                <span className="tw-bg-slate-100 tw-text-slate-700 tw-rounded-full tw-px-3 tw-py-1 tw-text-xs tw-font-bold">
                                                    {inviterHistory.logs.length}
                                                </span>
                                            </div>

                                            {inviterHistory.logs.length === 0 ? (
                                                <div className="tw-py-14 tw-px-4 tw-text-center tw-text-sm tw-text-slate-500">
                                                    Сүүлийн 7 хоногт урьж оруулсан машин олдсонгүй.
                                                </div>
                                            ) : (
                                                <div className="tw-overflow-x-auto">
                                                    <table className="tw-w-full tw-min-w-[850px] tw-text-left tw-border-collapse">
                                                        <thead className="tw-bg-slate-50">
                                                            <tr>
                                                                <th className="tw-px-4 tw-py-3 tw-text-[11px] tw-font-semibold tw-text-slate-500 tw-uppercase">
                                                                    №
                                                                </th>
                                                                <th className="tw-px-4 tw-py-3 tw-text-[11px] tw-font-semibold tw-text-slate-500 tw-uppercase">
                                                                    Зочны дугаар
                                                                </th>
                                                                <th className="tw-px-4 tw-py-3 tw-text-[11px] tw-font-semibold tw-text-slate-500 tw-uppercase">
                                                                    Орсон
                                                                </th>
                                                                <th className="tw-px-4 tw-py-3 tw-text-[11px] tw-font-semibold tw-text-slate-500 tw-uppercase">
                                                                    Гарсан
                                                                </th>
                                                                <th className="tw-px-4 tw-py-3 tw-text-[11px] tw-font-semibold tw-text-slate-500 tw-uppercase">
                                                                    Хугацаа
                                                                </th>
                                                                <th className="tw-px-4 tw-py-3 tw-text-[11px] tw-font-semibold tw-text-slate-500 tw-uppercase">
                                                                    Төлөв
                                                                </th>
                                                                <th className="tw-px-4 tw-py-3 tw-text-[11px] tw-font-semibold tw-text-slate-500 tw-uppercase tw-text-right">
                                                                    Төлбөр
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="tw-divide-y tw-divide-slate-100">
                                                            {inviterHistory.logs.map(
                                                                (
                                                                    historyLog,
                                                                    historyIndex
                                                                ) => {
                                                                    const guestPlate =
                                                                        normalizePlateKey(
                                                                            historyLog.car_plate ||
                                                                                historyLog.plate
                                                                        ) || "-";

                                                                    const historyPrice =
                                                                        getCleanPrice(
                                                                            historyLog.price
                                                                        );

                                                                    const historyStatus =
                                                                        String(
                                                                            historyLog.status ||
                                                                                ""
                                                                        )
                                                                            .trim()
                                                                            .toLowerCase();

                                                                    const isParked =
                                                                        !historyLog.exit_time ||
                                                                        historyStatus ===
                                                                            "parked";

                                                                    const paymentText =
                                                                        historyLog.paymentStatus ||
                                                                        (historyLog.payment
                                                                            ? "Төлсөн"
                                                                            : historyPrice > 0
                                                                              ? "Төлөөгүй"
                                                                              : "Төлбөргүй");

                                                                    return (
                                                                        <tr
                                                                            key={
                                                                                historyLog.id ||
                                                                                `${guestPlate}-${historyIndex}`
                                                                            }
                                                                            className="hover:tw-bg-slate-50/70"
                                                                        >
                                                                            <td className="tw-px-4 tw-py-3 tw-text-xs tw-text-slate-400">
                                                                                {historyIndex +
                                                                                    1}
                                                                            </td>
                                                                            <td className="tw-px-4 tw-py-3">
                                                                                <span className="tw-inline-flex tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-2.5 tw-py-1 tw-text-sm tw-font-bold tw-tracking-wider tw-text-slate-900">
                                                                                    {guestPlate}
                                                                                </span>
                                                                            </td>
                                                                            <td className="tw-px-4 tw-py-3 tw-text-xs sm:tw-text-sm tw-text-slate-700 tw-whitespace-nowrap">
                                                                                {formatDateTime(
                                                                                    historyLog.entry_time
                                                                                )}
                                                                            </td>
                                                                            <td className="tw-px-4 tw-py-3 tw-text-xs sm:tw-text-sm tw-text-slate-700 tw-whitespace-nowrap">
                                                                                {isParked
                                                                                    ? "-"
                                                                                    : formatDateTime(
                                                                                          historyLog.exit_time
                                                                                      )}
                                                                            </td>
                                                                            <td className="tw-px-4 tw-py-3 tw-text-xs sm:tw-text-sm tw-text-slate-500 tw-whitespace-nowrap">
                                                                                {historyLog.duration ||
                                                                                    "-"}
                                                                            </td>
                                                                            <td className="tw-px-4 tw-py-3">
                                                                                <span
                                                                                    className={`tw-inline-flex tw-rounded-full tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold ${
                                                                                        isParked
                                                                                            ? "tw-bg-emerald-100 tw-text-emerald-700"
                                                                                            : "tw-bg-slate-100 tw-text-slate-600"
                                                                                    }`}
                                                                                >
                                                                                    {isParked
                                                                                        ? "Зогсоолд"
                                                                                        : historyLog.status ||
                                                                                          "Гарсан"}
                                                                                </span>
                                                                            </td>
                                                                            <td className="tw-px-4 tw-py-3 tw-text-right tw-whitespace-nowrap">
                                                                                <div className="tw-text-sm tw-font-semibold tw-text-slate-900">
                                                                                    {historyPrice.toLocaleString()}
                                                                                    ₮
                                                                                </div>
                                                                                <div className="tw-text-[10px] tw-text-slate-400">
                                                                                    {paymentText}
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                }
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="tw-bg-white tw-border-t tw-border-slate-200 tw-px-4 sm:tw-px-6 tw-py-3 tw-flex tw-justify-end">
                                <button
                                    type="button"
                                    onClick={closeInviterHistory}
                                    className="tw-px-5 tw-py-2.5 tw-rounded-xl tw-bg-slate-900 tw-text-white tw-text-sm tw-font-medium hover:tw-bg-slate-800 tw-transition-colors"
                                >
                                    Хаах
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}